import * as Select from '@radix-ui/react-select';
import axios from 'axios';
import { addDays, format } from 'date-fns';
import React, { useEffect, useRef, useState } from 'react';
import { API_BASE } from './config';
// Add Material Icons imports
import EventIcon from '@mui/icons-material/Event';
import RepeatIcon from '@mui/icons-material/Repeat';
import SearchIcon from '@mui/icons-material/Search';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import CheckIcon from '@mui/icons-material/Check';
import { DateTime } from 'luxon';
// Import multi-app utilities
import {
  getAppIdForBox,
  createBoxToAppMapping,
  setBoxToAppMap,
  hasMultipleApplications,
  getApplicationDisplayName,
  getUserApplications,
} from './multiAppUtils';
import MultiAppDebug from './MultiAppDebug';
import BrandHeader from './BrandHeader';
const CLASSES_SELECTION_STORAGE_KEY = 'arboxer_classes_selection_v1';
const LEGACY_LAST_BOX_KEY = 'arboxer_last_box';
const LEGACY_LAST_LOCATION_KEY = 'arboxer_last_location';

const idsMatch = (left, right) => (
  left !== null
  && left !== undefined
  && right !== null
  && right !== undefined
  && String(left) === String(right)
);

const readLastClassesSelection = () => {
  try {
    const storedSelection = localStorage.getItem(CLASSES_SELECTION_STORAGE_KEY);
    if (storedSelection) {
      const parsed = JSON.parse(storedSelection);
      if (parsed && parsed.version === 1 && parsed.box_id !== undefined && parsed.box_id !== null) {
        return {
          app_id: parsed.app_id || null,
          box_id: parsed.box_id,
          location_id: parsed.location_id ?? null,
        };
      }
    }
  } catch (error) {
    console.error('Error parsing last classes selection:', error);
  }

  const legacyBox = localStorage.getItem(LEGACY_LAST_BOX_KEY);
  if (!legacyBox) return null;

  const legacyLocation = localStorage.getItem(LEGACY_LAST_LOCATION_KEY);
  return {
    app_id: null,
    box_id: legacyBox,
    location_id: legacyLocation || null,
  };
};

const writeLastClassesSelection = ({ appId, boxId, locationId }) => {
  if (boxId === null || boxId === undefined) return;

  try {
    localStorage.setItem(CLASSES_SELECTION_STORAGE_KEY, JSON.stringify({
      version: 1,
      app_id: appId || null,
      box_id: boxId,
      location_id: locationId ?? null,
      saved_at: new Date().toISOString(),
    }));
    localStorage.removeItem(LEGACY_LAST_BOX_KEY);
    localStorage.removeItem(LEGACY_LAST_LOCATION_KEY);
  } catch (error) {
    console.error('Error storing last classes selection:', error);
  }
};

// Replace formatTime to use Luxon 24-hour format
const formatTime = (timeString) => {
  if (!timeString) return '';
  // Use Luxon for 24-hour formatting
  const dt = DateTime.fromFormat(timeString, 'HH:mm');
  return dt.isValid ? dt.toFormat('HH:mm') : timeString;
};

// Helper functions to check if data fields are present and valid
// These check the actual data, not app-level flags, since features can vary by class/gym
const hasValidAvailability = (data) => {
  // Availability is valid if it's a number >= 0 (not null, undefined, or -1)
  return data.availability !== null && data.availability !== undefined && data.availability !== -1 && typeof data.availability === 'number';
};

const hasValidCredits = (data) => {
  // Credits are valid if creditPrice is present (not null/undefined)
  return data.creditPrice !== null && data.creditPrice !== undefined;
};

const hasBookedUsersList = (data) => {
  // booked_users is valid if it's an array (even if empty)
  return Array.isArray(data.booked_users);
};

const hasStandbyUsersList = (data) => {
  // schedule_stand_by is valid if it's an array (even if empty)
  return Array.isArray(data.schedule_stand_by);
};

const hasValidRegisteredCount = (data) => {
  // Registered count is valid if it's a number >= 0 (not null, undefined, or -1)
  return data.registered !== null && data.registered !== undefined && data.registered !== -1 && typeof data.registered === 'number';
};

const hasValidCapacity = (data) => {
  // Capacity is valid if it's a number > 0 (not null, undefined, -1, or '∞')
  const capacity = data.max_users || data.capacity;
  return capacity !== null && capacity !== undefined && capacity !== -1 && capacity !== '∞' && typeof capacity === 'number' && capacity > 0;
};

const getUserDisplayName = (user, fallback = 'Registered user') => {
  if (!user || typeof user !== 'object') return fallback;

  const nestedUser = user.user || user.friend_user || {};
  const firstName = user.first_name ?? nestedUser.first_name;
  const lastName = user.last_name ?? nestedUser.last_name;
  const name = (
    user.full_name
    || user.name
    || user.full_name_shorten
    || nestedUser.full_name
    || nestedUser.name
    || [firstName, lastName].filter(Boolean).join(' ')
    || fallback
  );

  return String(name).trim() || fallback;
};

const getUserInitials = (user, fallback = '?') => {
  const name = getUserDisplayName(user, '');
  if (!name) return fallback;
  return name
    .split(/\s+/)
    .filter(Boolean)
    .map(part => part[0])
    .join('')
    .slice(0, 2)
    .toUpperCase() || fallback;
};

const parseOptionalNumber = (value) => {
  if (value === null || value === undefined || value === '') return null;
  const numberValue = Number(value);
  return Number.isFinite(numberValue) ? numberValue : null;
};

const getBookingWindowInHours = (classItem) => (
  parseOptionalNumber(
    classItem?._raw?.booking_window_in_hours
      ?? classItem?._raw?.bookingPreOrderInHours
      ?? classItem?._raw?.enable_registration_time
      ?? classItem?.booking_window_in_hours
      ?? classItem?.bookingPreOrderInHours
      ?? classItem?.enable_registration_time
  )
);

const getClassDate = (classItem) => {
  const dateTime = classItem?._raw?.date_time;
  if (dateTime?.date) {
    const date = DateTime.fromFormat(
      dateTime.date,
      'yyyy-MM-dd HH:mm:ss',
      { zone: dateTime.timezone || 'Asia/Jerusalem' }
    );
    if (date.isValid) return date.toJSDate();
  }
  return new Date(`${classItem?._raw?.date}T${classItem?.start_time}`);
};

// Move mapApiResponseToClassItem to the top so it's defined before use
const mapApiResponseToClassItem = (apiData) => {
  const data = apiData.data || apiData;
  // Use data structure to determine name source: box_categories/series for Arbox-style, name directly for others
  const hasArboxStructure = !!(data.box_categories || data.series);
  return {
    name: hasArboxStructure ? (data.box_categories?.name || data.series?.series_name || 'Class') : (data.name || 'Class'),
    start_time: data.time || data.start_time,
    end_time: data.end_time,
    description: data.box_categories?.bio || '',
    capacity: data.max_users || data.capacity,
    registered: data.registered,
    trainer: data.coach?.full_name || '',
    user_booked: data.user_booked,
    user_in_standby: data.user_in_standby,
    stand_by_position: data.stand_by_position,
    booking_option: data.booking_option,
    stand_by: data.stand_by,
    schedule_id: data.schedule_id || null,
    schedule_type: data.schedule_type || null,
    excluded_from_schedule: data.excluded_from_schedule || false,
    // App-specific fields (will be undefined if not provided by the app)
    isFull: data.isFull,
    availability: data.availability,
    isNew: data.isNew,
    tooEarly: data.tooEarly,
    creditPrice: data.creditPrice,
    previousCreditPrice: data.previousCreditPrice,
    creditsLabel: data.creditsLabel,
    previousCreditsLabel: data.previousCreditsLabel,
    remainingCredits: data.remainingCredits,
    bookingPreOrderInHours: data.bookingPreOrderInHours,
    booking_window_in_hours: data.booking_window_in_hours ?? data.bookingPreOrderInHours ?? data.enable_registration_time,
    enable_registration_time: data.booking_window_in_hours ?? data.bookingPreOrderInHours ?? data.enable_registration_time,
    bookingRegistrationStart: data.bookingRegistrationStart,
    _raw: {
      ...data
    }
  };
};

// Utility to determine class timing info (isPast, isBookable, hoursUntilClass, etc.)
export function getClassTimingInfo(classItem) {
  const now = new Date();
  const classDate = getClassDate(classItem);
  const enableRegistrationHours = getBookingWindowInHours(classItem);
  const hoursUntilClass = (classDate - now) / (1000 * 60 * 60);
  const isPast = classDate < now;
  const isBookable = enableRegistrationHours !== null && (enableRegistrationHours === 0 || hoursUntilClass <= enableRegistrationHours);
  return { now, classDate, enableRegistrationHours, hoursUntilClass, isPast, isBookable };
}

// Utility to determine if a class is skippable (not in the past and not bookable)
export function getSingleScheduleDisabledAndReason(classItem) {
  const { isPast, isBookable } = getClassTimingInfo(classItem);
  const singleScheduleDisabled = isPast || isBookable;
  let singleScheduleReason = '';
  if (isPast) singleScheduleReason = 'This class is in the past.';
  else if (isBookable) singleScheduleReason = 'This class is already bookable. Single scheduling is only available before booking opens.';
  return { singleScheduleDisabled, singleScheduleReason };
}

function SchedulingModal({ classItem, onClose, onSchedule, loading, initialOption, jwt }) {
  const [selectedOption, setSelectedOption] = useState(initialOption || 'one-time');
  const [triggerDay, setTriggerDay] = useState(0); // 0=Sunday
  const [triggerTime, setTriggerTime] = useState('12:00');
  const [bookFollowing, setBookFollowing] = useState(false);
  // Check if debug mode is enabled from environment variable
  // React requires REACT_APP_ prefix for environment variables
  const debugEnabled = process.env.REACT_APP_ENABLE_SCHEDULE_DEBUG === '1';
  const [simulateLoading, setSimulateLoading] = useState(false);

  const { singleScheduleDisabled, singleScheduleReason } = getSingleScheduleDisabledAndReason(classItem);

  const bookingWindow = getBookingWindowInHours(classItem);
  const classTimezone = classItem._raw?.date_time?.timezone || 'Asia/Jerusalem';
  const classDateTimeStr = classItem._raw?.date_time?.date || '';
  // Parse class weekday and time
  const classDateObj = classDateTimeStr && classTimezone
    ? DateTime.fromFormat(classDateTimeStr, 'yyyy-MM-dd HH:mm:ss', { zone: classTimezone })
    : null;
  const classWeekday = classDateObj ? classDateObj.weekday % 7 : 0; // Luxon: 1=Mon, 7=Sun; JS: 0=Sun
  const classTime = classDateObj ? `${classDateObj.hour.toString().padStart(2, '0')}:${classDateObj.minute.toString().padStart(2, '0')}` : '09:00';

  // Helper to get next date string for a given weekday and time
  function getNextDateTimeString(weekday, time, timezone) {
    const now = DateTime.now().setZone(timezone);
    // Luxon: weekday 1=Mon, 7=Sun; JS: 0=Sun
    let luxonWeekday = weekday === 0 ? 7 : weekday; // convert JS Sunday(0) to Luxon Sunday(7)
    let daysToAdd = (luxonWeekday - now.weekday + 7) % 7;
    if (daysToAdd === 0 && time <= now.toFormat('HH:mm')) daysToAdd = 7;
    let nextDate = now.plus({ days: daysToAdd }).set({
      hour: Number(time.split(':')[0]),
      minute: Number(time.split(':')[1]),
      second: 0,
      millisecond: 0
    });
    return nextDate.toFormat('yyyy-MM-dd HH:mm:ss');
  }

  // Calculate trigger date (Luxon DateTime)
  const triggerDate = (() => {
    const now = DateTime.now().setZone(classTimezone);
    let luxonWeekday = triggerDay === 0 ? 7 : triggerDay;
    let daysToAdd = (luxonWeekday - now.weekday + 7) % 7;
    if (daysToAdd === 0 && triggerTime <= now.toFormat('HH:mm')) daysToAdd = 7;
    let d = now.plus({ days: daysToAdd }).set({
      hour: Number(triggerTime.split(':')[0]),
      minute: Number(triggerTime.split(':')[1]),
      second: 0,
      millisecond: 0
    });
    return d;
  })();

  // Helper to get next class datetime after trigger
  function getNextClassDateTime(triggerDate, classWeekday, classTime) {
    // triggerDate: Luxon DateTime
    let luxonWeekday = classWeekday === 0 ? 7 : classWeekday;
    let daysToAdd = (luxonWeekday - triggerDate.weekday + 7) % 7;
    if (daysToAdd === 0 && classTime <= triggerDate.toFormat('HH:mm')) daysToAdd = 7;
    let nextClassDate = triggerDate.plus({ days: daysToAdd }).set({
      hour: Number(classTime.split(':')[0]),
      minute: Number(classTime.split(':')[1]),
      second: 0,
      millisecond: 0
    });
    return nextClassDate;
  }

  const nextClassDate = getNextClassDateTime(triggerDate, classWeekday, classTime);
  let diffMs = nextClassDate.toMillis() - triggerDate.toMillis();
  let diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  let diffMinutes = Math.round((diffMs / (1000 * 60)) % 60);
  let displayClassDate = nextClassDate.toFormat('cccc, dd LLL yyyy HH:mm');

  // If bookFollowing is checked, add 7 days to nextClassDate and recalculate diff
  let min_hours_ahead = undefined;
  if (bookFollowing) {
    const nextNextClassDate = nextClassDate.plus({ days: 7 });
    diffMs = nextNextClassDate.toMillis() - triggerDate.toMillis();
    diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    diffMinutes = Math.round((diffMs / (1000 * 60)) % 60);
    displayClassDate = nextNextClassDate.toFormat('cccc, dd LLL yyyy HH:mm');
    min_hours_ahead = Math.max(0, Math.floor((diffMs / (1000 * 60 * 60)) - 1)); // minus 1 hour buffer
  }

  let diffDays = Math.floor(diffHours / 24);
  let displayTimeDiff = '';
  const hoursPart = diffHours % 24;
  if (diffDays > 0) {
    displayTimeDiff = `${diffDays} day${diffDays > 1 ? 's' : ''}`;
    if (hoursPart > 0) displayTimeDiff += `, ${hoursPart} hours`;
    if (diffMinutes > 0) displayTimeDiff += ` and ${diffMinutes} minutes`;
  } else {
    if (diffHours > 0) displayTimeDiff = `${diffHours} hours`;
    if (diffMinutes > 0) displayTimeDiff += (displayTimeDiff ? ' ' : '') + `${diffMinutes} minutes`;
    if (!displayTimeDiff) displayTimeDiff = '0 minutes';
  }


  const handleSchedule = () => {
    if (singleScheduleDisabled && selectedOption === 'one-time') return;
    if (bookingWindow === null) return;
    if (selectedOption === 'recurring' && bookingWindow === 0) {
      if (triggerDay === null || !triggerTime) return;
      onSchedule(selectedOption, {
        date: getNextDateTimeString(triggerDay, triggerTime, classTimezone),
        timezone: classTimezone,
        ...(min_hours_ahead !== undefined ? { min_hours_ahead } : {})
      });
      return;
    }
    onSchedule(selectedOption);
  };

  const handleSimulate = async (scheduleType) => {
    if (bookingWindow === null) {
      alert('Registration window is missing for this class. Please refresh and try again.');
      return;
    }
    setSimulateLoading(true);
    try {
      const appId = getAppIdForBox(classItem._raw.box_fk);
      
      // For Move apps, box_fk is a string like "venues/1666-A" and there's no location_id
      // For other apps, box_fk is an integer and location_id exists
      const isMoveApp = !!(classItem._raw.move_activity_id || typeof classItem._raw.box_fk === 'string');
      const locationId = isMoveApp 
        ? classItem._raw.box_fk  // For Move, use box_fk as location_id (or leave undefined since it's optional)
        : (classItem._raw.location_id || classItem._raw.locations_box_fk);

      let payload = {
        type: scheduleType === 'one-time' ? 'one_time' : 'recurring',
        box_id: classItem._raw.box_fk,
        class_name: classItem.name,
        booking_window_in_hours: bookingWindow,
        date_time: classItem._raw.date_time,
        app_id: appId,
      };
      
      // Only include location_id if it exists (for Arbox/Fizikal) or set it to box_id for Move
      if (locationId !== undefined && locationId !== null) {
        payload.location_id = locationId;
      }
      
      if (scheduleType === 'one-time') {
        payload.one_time = {
          class_id: classItem._raw.id,
        };
      } else {
        payload.pattern = {
          class_name: classItem.name,
        };
        if (selectedOption === 'recurring' && bookingWindow === 0) {
          const trigger_datetime = {
            date: getNextDateTimeString(triggerDay, triggerTime, classTimezone),
            timezone: classTimezone,
            ...(min_hours_ahead !== undefined ? { min_hours_ahead } : {})
          };
          if (trigger_datetime.date && trigger_datetime.timezone) {
            payload.pattern.trigger_datetime = {
              date: trigger_datetime.date,
              timezone: trigger_datetime.timezone
            };
          }
          if (trigger_datetime.min_hours_ahead !== undefined) {
            payload.pattern.min_hours_ahead = trigger_datetime.min_hours_ahead;
          }
        }
      }

      const resp = await axios.post(
        `${API_BASE}/schedule/simulate`,
        payload,
        {
          headers: { Authorization: `Bearer ${jwt}` },
        }
      );
      
      const result = resp.data.booking_result;
      let message = `Simulation ${result.status}: `;
      if (result.status === 'success') {
        message += `Successfully booked ${result.successful_bookings || 0} class(es)`;
      } else if (result.status === 'error') {
        message += result.error || 'Unknown error';
      } else if (result.status === 'skipped') {
        message += result.reason || 'Skipped';
      } else {
        message += JSON.stringify(result);
      }
      
      alert(message);
    } catch (err) {
      const errorMsg = err?.response?.data?.detail || err?.message || 'Failed to simulate schedule';
      alert(`Simulation failed: ${errorMsg}`);
    } finally {
      setSimulateLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40 p-4" onClick={onClose}>
      <div className="bg-white rounded-lg shadow-lg max-w-md w-full flex flex-col h-[90vh] p-0" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="text-center p-6 border-b border-gray-200 sticky top-0 bg-white z-10">
          <h2 className="text-2xl font-bold text-gray-800 mb-2">Schedule Class</h2>
          <p className="text-gray-600">Choose how you'd like to schedule this class</p>
        </div>
        {/* Scrollable Content */}
        <div className="space-y-4 flex-1 overflow-y-auto p-6">
          {/* One Time Option */}
          <div
            className={`border-2 rounded-lg p-4 cursor-pointer transition-all duration-200 flex flex-col ${selectedOption === 'one-time' ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'} ${singleScheduleDisabled ? 'opacity-50 cursor-not-allowed' : ''}`}
            onClick={() => { if (!singleScheduleDisabled) setSelectedOption('one-time'); }}
          >
            <div className="flex items-start gap-3">
              <div className="text-2xl"><EventIcon fontSize="inherit" /></div>
              <div className="flex-1">
                <h3 className="font-semibold text-gray-800 mb-1">Schedule This Class Only</h3>
                <p className="text-sm text-gray-600">Book just this specific instance of the class</p>
                {singleScheduleDisabled && (
                  <div className="text-xs text-red-500 mt-2">{singleScheduleReason}</div>
                )}
              </div>
              <div className="w-5 h-5 flex items-center justify-center">
                {selectedOption === 'one-time' && !singleScheduleDisabled ? (
                  <div className="bg-blue-500 rounded-full w-5 h-5 flex items-center justify-center">
                    <div className="w-2 h-2 bg-white rounded-full"></div>
                  </div>
                ) : (
                  <div className="w-5 h-5 invisible"></div>
                )}
              </div>
            </div>
          </div>

          {/* Recurring Option */}
          <div
            className={`border-2 rounded-lg p-4 cursor-pointer transition-all duration-200 flex flex-col ${selectedOption === 'recurring' ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'}`}
            onClick={() => setSelectedOption('recurring')}
          >
            <div className="flex items-start gap-3">
              <div className="text-2xl"><RepeatIcon fontSize="inherit" /></div>
              <div className="flex-1">
                <h3 className="font-semibold text-gray-800 mb-1">Schedule Recurring Series</h3>
                <p className="text-sm text-gray-600">Book this class every week at the same time</p>
              </div>
              <div className="w-5 h-5 flex items-center justify-center">
                {selectedOption === 'recurring' ? (
                  <div className="bg-blue-500 rounded-full w-5 h-5 flex items-center justify-center">
                    <div className="w-2 h-2 bg-white rounded-full"></div>
                  </div>
                ) : (
                  <div className="w-5 h-5 invisible"></div>
                )}
              </div>
            </div>
            {selectedOption === 'recurring' && bookingWindow === 0 && (
              <div className="mt-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">Choose trigger day:</label>
                <select
                  className="w-full border rounded p-2 mb-2"
                  value={triggerDay}
                  onChange={e => setTriggerDay(Number(e.target.value))}
                >
                  {['Sunday','Monday','Tuesday','Wednesday','Thursday','Friday','Saturday'].map((d, i) => (
                    <option value={i} key={i}>{d}</option>
                  ))}
                </select>
                <label className="block text-sm font-medium text-gray-700 mb-1">Choose trigger time:</label>
                <input
                  type="time"
                  className="w-full border rounded p-2"
                  value={triggerTime}
                  onChange={e => setTriggerTime(e.target.value)}
                />
                <div className="text-xs text-gray-500 mt-1">
                  This is when the automation will run each week to book your class.
                </div>
                <div className="mt-2 text-sm text-blue-700">
                  The next class to be booked is on <b>{displayClassDate}</b>, which is <b>{displayTimeDiff}</b> after your trigger.
                </div>
                <label className="flex items-center mt-2 gap-2">
                  <input type="checkbox" checked={bookFollowing} onChange={e => setBookFollowing(e.target.checked)} />
                  Book the following class instead
                </label>
              </div>
            )}
          </div>
        </div>
        {/* Action Buttons */}
        <div className="flex flex-col gap-3 p-6 border-t border-gray-200 sticky bottom-0 bg-white z-10">
          {/* Debug buttons - only show when debug mode is enabled */}
          {debugEnabled && (
            <div className="flex gap-2 pb-2 border-b border-gray-200">
              <button
                onClick={() => handleSimulate('one-time')}
                className="flex-1 py-2 px-3 bg-yellow-500 text-white rounded-lg text-sm font-semibold hover:bg-yellow-600 transition-colors"
                disabled={simulateLoading || loading}
                title="Create a one-time schedule and immediately trigger booking"
              >
                {simulateLoading ? 'Simulating...' : '🔧 Simulate One-Time'}
              </button>
              <button
                onClick={() => handleSimulate('recurring')}
                className="flex-1 py-2 px-3 bg-orange-500 text-white rounded-lg text-sm font-semibold hover:bg-orange-600 transition-colors"
                disabled={simulateLoading || loading}
                title="Create a recurring schedule and immediately trigger booking"
              >
                {simulateLoading ? 'Simulating...' : '🔧 Simulate Recurring'}
              </button>
            </div>
          )}
          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="flex-1 py-3 px-4 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSchedule}
              className="flex-1 py-3 px-4 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 transition-colors"
              disabled={loading || simulateLoading || bookingWindow === null || (singleScheduleDisabled && selectedOption === 'one-time')}
            >
              {loading ? (
                <span className="flex items-center justify-center">
                  <svg className="animate-spin h-5 w-5 mr-2 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"></path>
                  </svg>
                  Scheduling...
                </span>
              ) : (
                'Schedule'
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function ClassDetailsPane({ classItem, onClose, setError, jwt, updateClassData, isClassScheduled, refreshSchedulesAndClasses, selectedLocation, selectedDate }) {
  const [showScheduling, setShowScheduling] = useState(false);
  const modalRef = useRef(null);
  const [bookingState, setBookingState] = useState({ booked: !!classItem.user_booked, error: '' });
  const [bookingLoading, setBookingLoading] = useState(false);
  const [toast, setToast] = useState({ message: '', type: '' });
  const [scheduleLoading, setScheduleLoading] = useState(false);
  const [showRecurringCancelModal, setShowRecurringCancelModal] = useState(false);
  const [cancelLoading] = useState(false);
  const [showRecurringScheduling, setShowRecurringScheduling] = useState(false);
  const [logbookComment, setLogbookComment] = useState('');

  useEffect(() => {
    if (showRecurringCancelModal) return; // Don't attach handler if modal is open
    function handleClickOutside(event) {
      if (modalRef.current && !modalRef.current.contains(event.target)) {
        onClose();
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [onClose, showRecurringCancelModal]);

  useEffect(() => {
    setBookingState({ booked: !!classItem.user_booked, error: '' });
  }, [classItem]);

  // Fetch logbook data for the class date
  useEffect(() => {
    const fetchLogbook = async () => {
      if (!classItem || !selectedDate || !jwt) {
        setLogbookComment('');
        return;
      }

      try {
        // Get the app_id for this box
        const appId = getAppIdForBox(classItem._raw.box_fk);

        // Fetch logbook for the selected date
        const res = await axios.post(
          `${API_BASE}/logbook/workouts`,
          { date: selectedDate },
          {
            headers: { Authorization: `Bearer ${jwt}` },
            params: { app_id: appId }
          }
        );

        // The response structure is nested arrays: data[][][]
        // Flatten and find the workout matching this class
        const logbookData = res.data.data || [];
        let foundComment = '';

        // Flatten the nested array structure
        for (let sectionIdx = 0; sectionIdx < logbookData.length; sectionIdx++) {
          const section = logbookData[sectionIdx];
          if (Array.isArray(section)) {
            for (let subsectionIdx = 0; subsectionIdx < section.length; subsectionIdx++) {
              const subsection = section[subsectionIdx];
              if (Array.isArray(subsection)) {
                for (const workout of subsection) {
                  // Match by box_category_fk
                  const matches = workout.box_category_fk === classItem._raw.box_category_fk;

                  if (matches) {
                    if (workout.comment && workout.comment.trim()) {
                      foundComment = workout.comment;
                      break;
                    }
                  }
                }
              }
            }
          }
        }

        setLogbookComment(foundComment);
      } catch (err) {
        // Silently fail - logbook is optional
        if (process.env.NODE_ENV === 'development') {
          // Logbook fetch failed silently (optional feature)
        }
        setLogbookComment('');
      }
    };

    fetchLogbook();
  }, [classItem, selectedDate, jwt]);

  if (!classItem) return null;
  
  const bookedUsers = classItem._raw?.booked_users || [];
  const standbyUsers = classItem._raw?.schedule_stand_by || [];
  const description = classItem._raw?.box_categories?.bio || '';
  const capacity = classItem.capacity || classItem.max_users;
  // Use helper functions to check data validity
  const registeredCount = classItem.registered;
  const capacityKnown = hasValidCapacity(classItem._raw);

  const handleSchedule = async (scheduleType, trigger_datetime) => {
    const bookingWindow = getBookingWindowInHours(classItem);
    if (bookingWindow === null) {
      setError('Registration window is missing for this class. Please refresh and try again.');
      setToast({ message: 'Registration window is missing for this class. Please refresh and try again.', type: 'error' });
      setShowScheduling(false);
      setShowRecurringScheduling(false);
      return;
    }
    // Only block single (one-time) scheduling for bookable classes
    if (scheduleType === 'one-time' && bookingWindow === 0) {
      setError('This class can be booked right away and does not support single scheduling.');
      setToast({ message: 'This class can be booked right away and does not support single scheduling.', type: 'error' });
      setShowScheduling(false);
      setShowRecurringScheduling(false);
      return;
    }
    setScheduleLoading(true);
    setError(null);
    setToast({ message: '', type: '' });
    try {
      // Get the app_id for this box
      const appId = getAppIdForBox(classItem._raw.box_fk);
      
      // For Move apps, box_fk is a string like "venues/1666-A" and there's no location_id
      // For other apps, box_fk is an integer and location_id exists
      const isMoveApp = !!(classItem._raw.move_activity_id || typeof classItem._raw.box_fk === 'string');
      const locationId = isMoveApp 
        ? classItem._raw.box_fk  // For Move, use box_fk as location_id (or leave undefined since it's optional)
        : (classItem._raw.location_id || selectedLocation);

      let payload = {
        type: scheduleType === 'one-time' ? 'one_time' : 'recurring',
        box_id: classItem._raw.box_fk,
        class_name: classItem.name,
        booking_window_in_hours: bookingWindow,
        date_time: classItem._raw.date_time, // top-level date_time
        app_id: appId, // Include app_id for multi-app support
      };
      
      // Only include location_id if it exists (for Arbox/Fizikal) or set it to box_id for Move
      if (locationId !== undefined && locationId !== null) {
        payload.location_id = locationId;
      }
      if (scheduleType === 'one-time') {
        payload.one_time = {
          class_id: classItem._raw.id,
        };
      } else {
        payload.pattern = {
          class_name: classItem.name,
        };
        if (trigger_datetime) {
          if (trigger_datetime.date && trigger_datetime.timezone) {
            payload.pattern.trigger_datetime = {
              date: trigger_datetime.date,
              timezone: trigger_datetime.timezone
            };
          }
          if (trigger_datetime.min_hours_ahead !== undefined) {
            payload.pattern.min_hours_ahead = trigger_datetime.min_hours_ahead;
          }
        }
      }
      const resp = await axios.post(
        `${API_BASE}/schedule/create`,
        payload,
        {
          headers: { Authorization: `Bearer ${jwt}` },
        }
      );
      setToast({ message: 'Schedule created successfully!', type: 'success' });
      // Use the response to update only the relevant class item
      const scheduleData = resp.data;
      // Update the class item with schedule_id and schedule_type
      const updatedClassItem = {
        ...classItem,
        schedule_id: scheduleData.schedule_id,
        schedule_type: scheduleData.type,
        // Optionally, you can add more fields if needed
      };
      if (updateClassData) updateClassData(classItem._raw.id, updatedClassItem);
      setShowScheduling(false);
      setShowRecurringScheduling(false);
      // Do NOT call refreshSchedulesAndClasses or close the pane
    } catch (err) {
      let errorMsg = 'Failed to schedule class.';
      if (err?.response?.data?.detail) {
        errorMsg = err.response.data.detail;
      } else if (err?.message) {
        errorMsg = err.message;
      }
      setError(errorMsg);
      setToast({ message: errorMsg, type: 'error' });
    } finally {
      setScheduleLoading(false);
    }
  };

  const handleCancelSchedule = async () => {
    // If this is a recurring schedule, show the modal to choose cancel type
    if (classItem.schedule_type === 'recurring') {
      setShowRecurringCancelModal(true);
      return;
    }
    if (!classItem.schedule_id && !classItem._raw.schedule_id) {
      setError('No schedule_id found for this class.');
      setToast({ message: 'No schedule_id found for this class.', type: 'error' });
      return;
    }
    setScheduleLoading(true);
    setError(null);
    setToast({ message: '', type: '' });
    try {
      const scheduleId = classItem.schedule_id || classItem._raw.schedule_id;
      await axios.post(
        `${API_BASE}/schedule/cancelSchedule`,
        { schedule_id: scheduleId },
        {
          headers: { Authorization: `Bearer ${jwt}` },
        }
      );
      setToast({ message: 'Schedule cancelled successfully!', type: 'success' });
      // Mark the class as not scheduled in the details pane
      classItem.schedule_id = null;
      setShowScheduling(false);
      // Update the class in the main classes list
      if (updateClassData) {
        updateClassData(classItem._raw.id, { ...classItem, schedule_id: null });
      }
      // Do NOT close the scheduling modal or details pane, and do NOT refresh schedules/classes
      // await refreshSchedulesAndClasses(); // <-- Remove this line to avoid jarring refresh
    } catch (err) {
      let errorMsg = 'Failed to cancel schedule.';
      if (err?.response?.data?.detail) {
        errorMsg = err.response.data.detail;
      } else if (err?.message) {
        errorMsg = err.message;
      }
      setError(errorMsg);
      setToast({ message: errorMsg, type: 'error' });
    } finally {
      setScheduleLoading(false);
    }
  };

  // Handler for modal actions
  const handleRecurringCancelAction = async (action) => {
    setShowRecurringCancelModal(false);
    if (action === 'entire') {
      // Cancel the entire recurring schedule
      setScheduleLoading(true);
      setError(null);
      setToast({ message: '', type: '' });
      try {
        const scheduleId = classItem.schedule_id || classItem._raw.schedule_id;
        await axios.post(
          `${API_BASE}/schedule/cancelSchedule`,
          { schedule_id: scheduleId },
          {
            headers: { Authorization: `Bearer ${jwt}` },
          }
        );
        setToast({ message: 'Recurring schedule cancelled successfully!', type: 'success' });
        // Mark the class as not scheduled in the details pane
        classItem.schedule_id = null;
        classItem.schedule_type = null;
        setShowScheduling(false);
        // Update the class in the main classes list
        if (updateClassData) {
          updateClassData(classItem._raw.id, { ...classItem, schedule_id: null, schedule_type: null });
        }
      } catch (err) {
        let errorMsg = 'Failed to cancel recurring schedule.';
        if (err?.response?.data?.detail) {
          errorMsg = err.response.data.detail;
        } else if (err?.message) {
          errorMsg = err.message;
        }
        setError(errorMsg);
        setToast({ message: errorMsg, type: 'error' });
      } finally {
        setScheduleLoading(false);
      }
    } else if (action === 'skip') {
      // Skip this class occurrence only
      setScheduleLoading(true);
      setError(null);
      setToast({ message: '', type: '' });
      try {
        const scheduleId = classItem.schedule_id || classItem._raw.schedule_id;
        const classId = classItem._raw.id;
        await axios.post(
          `${API_BASE}/schedule/skipOccurrence`,
          { schedule_id: scheduleId, class_id: classId, class_datetime: classItem._raw.date_time },
          {
            headers: { Authorization: `Bearer ${jwt}` },
          }
        );
        setToast({ message: 'This class occurrence was skipped!', type: 'success' });
        // Update the class in the main classes list and details pane
        if (updateClassData) updateClassData(classItem._raw.id, { ...classItem, excluded_from_schedule: true });
        // Optionally, you could update the UI to reflect the skip (e.g., mark as skipped)
      } catch (err) {
        let errorMsg = 'Failed to skip this class occurrence.';
        if (err?.response?.data?.detail) {
          errorMsg = err.response.data.detail;
        } else if (err?.message) {
          errorMsg = err.message;
        }
        setError(errorMsg);
        setToast({ message: errorMsg, type: 'error' });
      } finally {
        setScheduleLoading(false);
      }
    }
  };

  const handleCancelBooking = async () => {
    try {
      setBookingLoading(true);
      setError(null);
      setBookingState({ ...bookingState, error: '' });
      
      // Get the app_id for this box
      const appId = getAppIdForBox(classItem._raw.box_fk);
      
      // Check if this is a Move app
      const isMoveApp = !!(classItem._raw.move_activity_id || typeof classItem._raw.box_fk === 'string');
      
      let cancelRequest;
      
      if (isMoveApp) {
        // For Move apps, we need the booking_id
        // The booking_id should be included in the schedule response from the backend
        // It should be a string like "bookings/7503588-B", not a number
        const bookingId = classItem._raw.move_booking_id;
        if (!bookingId) {
          throw new Error('Booking ID not found. Please refresh the page and try again.');
        }
        // Validate that booking_id is a string (not a number)
        if (typeof bookingId !== 'string' || !bookingId.startsWith('bookings/')) {
          throw new Error(`Invalid booking ID format: ${bookingId}. Please refresh the page and try again.`);
        }
        
        cancelRequest = {
          booking_id: bookingId,
          schedule_id: classItem._raw.id,
          app_id: appId,
          // Include activity_id, activity_time, and box_id for Move apps to fetch updated class
          activity_id: classItem._raw.move_activity_id,
          activity_time: classItem._raw.move_event_time,
          box_id: classItem._raw.box_fk,
        };
      } else {
        // For Arbox/Fizikal apps, use schedule_user_id
        // user_booked contains the schedule_user_id when the user is booked
        const schedule_user_id = classItem.user_booked;
        if (!schedule_user_id) {
          throw new Error('Could not find your booking information');
        }
        
        cancelRequest = {
          schedule_user_id: schedule_user_id,
          schedule_id: classItem._raw.id,
          app_id: appId,
        };
      }

      const resp = await axios.post(
        `${API_BASE}/schedule/cancel`,
        cancelRequest,
        {
          headers: { Authorization: `Bearer ${jwt}` },
        }
      );
      
      // Update the class item with the response data
      const updatedClassData = resp.data.data || resp.data;
      
      // Create a completely new class item with all fresh data
      const updatedClassItem = mapApiResponseToClassItem(updatedClassData);
      setToast({ message: 'Class cancelled successfully!', type: 'success' });
      setBookingState({ booked: !!updatedClassItem.user_booked, error: '' });
      
      // Update the schedule with the new data
      if (updateClassData) updateClassData(classItem._raw.id, updatedClassItem);
    } catch (err) {
      let errorMsg = 'Failed to cancel class.';
      let statusCode = err?.response?.data?.statusCode || err?.response?.status;
      const messageToUser = err?.response?.data?.error?.messageToUser;
      if (Array.isArray(messageToUser) && messageToUser.length > 0) {
        errorMsg = messageToUser.map(m => m.message).join(' ');
      } else if (err?.response?.data?.error?.message) {
        errorMsg = err.response.data.error.message;
      } else if (err?.response?.data?.detail) {
        errorMsg = err.response.data.detail;
      } else if (err?.message) {
        errorMsg = err.message;
      }
      if (statusCode) {
        errorMsg = `[${statusCode}] ${errorMsg}`;
      }
      setError(errorMsg);
      setBookingState({ ...bookingState, error: errorMsg });
      setToast({ message: errorMsg, type: 'error' });
    } finally {
      setBookingLoading(false);
    }
  };

  const getBookingButton = () => {
    if (!classItem.booking_option || classItem.booking_option === 'past') {
      return null;
    }

    const { isBookable } = getClassTimingInfo(classItem);
    if (!isBookable) {
      // For future classes, let the bottom section render the Schedule button only
      return null;
    }

    // Feature-based checks (works for any app that provides these fields)
    // Don't show Book button if class is full (if app provides isFull field)
    if (classItem.isFull === true) {
      return null;
    }
    // Don't show Book button if too early (if app provides tooEarly field)
    if (classItem.tooEarly === true) {
      return null;
    }
    // Check if user has enough credits (if app uses credit system)
    if (classItem.creditPrice !== null && classItem.creditPrice !== undefined) {
      if (classItem.remainingCredits === null || classItem.remainingCredits === undefined) {
        // Credits info not available, still allow booking attempt
      } else if (classItem.remainingCredits < classItem.creditPrice) {
        // Not enough credits - don't show Book button
        return null;
      }
    }

    switch (classItem.booking_option) {
      case 'insertScheduleUser':
        // Check if class is actually booked (use classItem.user_booked as source of truth)
        const isBooked = bookingState.booked || classItem.user_booked;
        if (isBooked) {
          return (
            <button
              className="w-full bg-red-600 text-white py-3 px-4 rounded-lg font-semibold hover:bg-red-700 transition-colors"
              onClick={handleCancelBooking}
              disabled={bookingLoading}
            >
              {bookingLoading ? (
                <span className="flex items-center justify-center">
                  <svg className="animate-spin h-5 w-5 mr-2 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"></path>
                  </svg>
                  Cancelling...
                </span>
              ) : (
                'Cancel Booking'
              )}
            </button>
          );
        }
        return (
          <button
            className="w-full bg-gradient-to-r from-emerald-600 to-green-600 text-white py-3 px-4 rounded-lg font-semibold shadow-sm hover:shadow-md hover:brightness-105 transition-all flex items-center justify-center"
            onClick={async () => {
              try {
                setBookingLoading(true);
                setError(null);
                setBookingState({ ...bookingState, error: '' });
                
                // Double-check that class is not already booked before attempting to book
                if (classItem.user_booked || bookingState.booked) {
                  throw new Error('This class is already booked. Please refresh the page.');
                }
                
                // Get the app_id for this box
                let appId = getAppIdForBox(classItem._raw.box_fk);
                
                // Fallback: if box-to-app mapping returns 'default' but user has no default app,
                // try to find the correct app by checking user applications
                if (appId === 'default') {
                  const userApps = getUserApplications();
                  const hasDefaultApp = userApps.some(app => app.app_id === 'default');
                  if (!hasDefaultApp && userApps.length > 0) {
                    // Use the first available app as fallback
                    appId = userApps[0].app_id;
                    if (process.env.NODE_ENV === 'development') {
                      console.warn('⚠️ Using fallback app_id:', appId, 'for box:', classItem._raw.box_fk, '- default app not found');
                    }
                  }
                }

                // Prepare booking request
                const bookingRequest = {
                  box_id: classItem._raw.box_fk,
                  schedule_id: classItem._raw.id,
                  app_id: appId,  // Include app_id for correct JWT selection
                };

                // For Move apps, include activity_id and activity_time
                if (classItem._raw.move_activity_id && classItem._raw.move_event_time) {
                  bookingRequest.activity_id = classItem._raw.move_activity_id;
                  // Convert move_event_time to the format expected by Move API
                  // move_event_time is in format "2025-12-05T07:00:00", convert to "2025-12-05 07:00:00.000"
                  const moveTime = classItem._raw.move_event_time;
                  if (moveTime.includes('T')) {
                    const [date, time] = moveTime.split('T');
                    const timePart = time.split('.')[0]; // Remove milliseconds if present
                    bookingRequest.activity_time = `${date} ${timePart}.000`;
                  } else {
                    bookingRequest.activity_time = moveTime;
                  }
                }

                const resp = await axios.post(
                  `${API_BASE}/schedule/book`,
                  bookingRequest,
                  {
                    headers: { Authorization: `Bearer ${jwt}` },
                  }
                );
                
                // Update the class item with the response data
                const updatedClassData = resp.data.data || resp.data;
                
                // For Move apps, the booking ID should already be in move_booking_id from the backend
                // Don't overwrite it with the numeric id field (which is the schedule ID, not booking ID)
                
                // Create a completely new class item with all fresh data
                const updatedClassItem = mapApiResponseToClassItem(updatedClassData);
                
                setToast({ message: 'Class booked successfully!', type: 'success' });
                setBookingState({ booked: !!updatedClassItem.user_booked, error: '' });
                
                // Update the schedule with the new data
                if (updateClassData) updateClassData(classItem._raw.id, updatedClassItem);
              } catch (err) {
                let errorMsg = 'Failed to book class.';
                let statusCode = err?.response?.data?.statusCode || err?.response?.status;
                const messageToUser = err?.response?.data?.error?.messageToUser;
                if (Array.isArray(messageToUser) && messageToUser.length > 0) {
                  errorMsg = messageToUser.map(m => m.message).join(' ');
                } else if (err?.response?.data?.error?.message) {
                  errorMsg = err.response.data.error.message;
                } else if (err?.response?.data?.detail) {
                  errorMsg = err.response.data.detail;
                } else if (err?.message) {
                  errorMsg = err.message;
                }
                if (statusCode) {
                  errorMsg = `[${statusCode}] ${errorMsg}`;
                }
                setError(errorMsg);
                setBookingState({ ...bookingState, error: errorMsg });
                setToast({ message: errorMsg, type: 'error' });
              } finally {
                setBookingLoading(false);
              }
            }}
            disabled={bookingLoading}
          >
            {bookingLoading ? (
              <span className="flex items-center justify-center">
                <svg className="animate-spin h-5 w-5 mr-2 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"></path>
                </svg>
                Booking...
              </span>
            ) : (
              'Book Class'
            )}
          </button>
        );
      case 'insertStandby':
        return (
          <button 
            className="w-full bg-gradient-to-r from-amber-500 to-orange-500 text-white py-3 px-4 rounded-lg font-semibold shadow-sm hover:shadow-md hover:brightness-105 transition-all flex items-center justify-center"
            onClick={async () => {
              try {
                setBookingLoading(true);
                setError(null);
                setBookingState({ ...bookingState, error: '' });
                // Get the app_id for this box
                const appId = getAppIdForBox(classItem._raw.box_fk);
                const classDateTime = classItem._raw.date_time || (
                  classItem._raw.date && classItem._raw.time
                    ? { date: `${classItem._raw.date} ${classItem._raw.time}`, timezone: 'Asia/Jerusalem' }
                    : undefined
                );

                const resp = await axios.post(
                  `${API_BASE}/schedule/standby/book`,
                  {
                    box_id: classItem._raw.box_fk,
                    schedule_id: classItem._raw.id,
                    app_id: appId,  // Include app_id for correct JWT selection
                    class_id: classItem._raw.id,
                    class_name: classItem.name,
                    class_datetime: classDateTime,
                    location_id: classItem._raw.location_id,
                  },
                  {
                    headers: { Authorization: `Bearer ${jwt}` },
                  }
                );
                
                // Update the class item with the response data
                const updatedClassData = resp.data.data || resp.data;
                const mergedClassData = {
                  ...classItem._raw,
                  ...updatedClassData,
                  id: classItem._raw.id,
                  box_fk: classItem._raw.box_fk,
                  date_time: classItem._raw.date_time,
                  date: classItem._raw.date,
                  time: classItem._raw.time,
                  location_id: classItem._raw.location_id,
                };
                
                // Preserve class context needed for later standby cancellation.
                const updatedClassItem = mapApiResponseToClassItem(mergedClassData);
                
                setToast({ message: 'Joined standby successfully!', type: 'success' });
                setBookingState({ booked: !!updatedClassItem.user_in_standby, error: '' });
                
                // Update the schedule with the new data
                if (updateClassData) updateClassData(classItem._raw.id, updatedClassItem);
              } catch (err) {
                let errorMsg = 'Failed to join standby.';
                let statusCode = err?.response?.data?.statusCode || err?.response?.status;
                const messageToUser = err?.response?.data?.error?.messageToUser;
                if (Array.isArray(messageToUser) && messageToUser.length > 0) {
                  errorMsg = messageToUser.map(m => m.message).join(' ');
                } else if (err?.response?.data?.error?.message) {
                  errorMsg = err.response.data.error.message;
                } else if (err?.response?.data?.detail) {
                  errorMsg = err.response.data.detail;
                } else if (err?.message) {
                  errorMsg = err.message;
                }
                if (statusCode) {
                  errorMsg = `[${statusCode}] ${errorMsg}`;
                }
                setError(errorMsg);
                setBookingState({ ...bookingState, error: errorMsg });
                setToast({ message: errorMsg, type: 'error' });
              } finally {
                setBookingLoading(false);
              }
            }}
            disabled={bookingLoading}
          >
            {bookingLoading ? (
              <span className="flex items-center justify-center">
                <svg className="animate-spin h-5 w-5 mr-2 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"></path>
                </svg>
                Joining...
              </span>
            ) : (
              'Join Standby'
            )}
          </button>
        );
      case 'cancelScheduleUser':
        return (
          <button
            className="w-full bg-gradient-to-r from-red-500 to-red-600 text-white py-3 px-4 rounded-lg font-semibold shadow-sm hover:shadow-md hover:brightness-105 transition-all"
            onClick={handleCancelBooking}
            disabled={bookingLoading}
          >
            {bookingLoading ? (
              <span className="flex items-center justify-center">
                <svg className="animate-spin h-5 w-5 mr-2 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"></path>
                </svg>
                Cancelling...
              </span>
            ) : (
              'Cancel Booking'
            )}
          </button>
        );
      case 'cancelWaitList':
        return (
          <button
            className="w-full bg-gradient-to-r from-red-500 to-red-600 text-white py-3 px-4 rounded-lg font-semibold shadow-sm hover:shadow-md hover:brightness-105 transition-all flex items-center justify-center"
            onClick={async () => {
              try {
                setBookingLoading(true);
                setError(null);
                setBookingState({ ...bookingState, error: '' });
                
                // user_in_standby contains the schedule_stand_by_id when the user is in standby
                const schedule_stand_by_id = classItem.user_in_standby;
                if (!schedule_stand_by_id) {
                  throw new Error('Could not find your standby information');
                }
                
                // Get the app_id for this box
                const appId = getAppIdForBox(classItem._raw.box_fk);
                const classDateTime = classItem._raw.date_time || (
                  classItem._raw.date && classItem._raw.time
                    ? { date: `${classItem._raw.date} ${classItem._raw.time}`, timezone: 'Asia/Jerusalem' }
                    : undefined
                );

                const resp = await axios.post(
                  `${API_BASE}/schedule/standby/cancel`,
                  {
                    schedule_stand_by_id: schedule_stand_by_id,
                    app_id: appId,  // Include app_id for correct JWT selection
                    schedule_id: classItem._raw.id,
                    class_id: classItem._raw.id,
                    class_name: classItem.name,
                    class_datetime: classDateTime,
                    box_id: classItem._raw.box_fk,
                    location_id: classItem._raw.location_id,
                  },
                  {
                    headers: { Authorization: `Bearer ${jwt}` },
                  }
                );
                
                // Update the class item with the response data
                const updatedClassData = resp.data.data || resp.data;
                const mergedClassData = {
                  ...classItem._raw,
                  ...updatedClassData,
                  id: classItem._raw.id,
                  box_fk: classItem._raw.box_fk,
                  date_time: classItem._raw.date_time,
                  date: classItem._raw.date,
                  time: classItem._raw.time,
                  location_id: classItem._raw.location_id,
                };
                
                // Preserve class context after standby cancellation as well.
                const updatedClassItem = mapApiResponseToClassItem(mergedClassData);
                
                setToast({ message: 'Cancelled standby successfully!', type: 'success' });
                setBookingState({ booked: false, error: '' });
                
                // Update the schedule with the new data
                if (updateClassData) updateClassData(classItem._raw.id, updatedClassItem);
              } catch (err) {
                let errorMsg = 'Failed to cancel standby.';
                let statusCode = err?.response?.data?.statusCode || err?.response?.status;
                const messageToUser = err?.response?.data?.error?.messageToUser;
                if (Array.isArray(messageToUser) && messageToUser.length > 0) {
                  errorMsg = messageToUser.map(m => m.message).join(' ');
                } else if (err?.response?.data?.error?.message) {
                  errorMsg = err.response.data.error.message;
                } else if (err?.response?.data?.detail) {
                  errorMsg = err.response.data.detail;
                } else if (err?.message) {
                  errorMsg = err.message;
                }
                if (statusCode) {
                  errorMsg = `[${statusCode}] ${errorMsg}`;
                }
                setError(errorMsg);
                setBookingState({ ...bookingState, error: errorMsg });
                setToast({ message: errorMsg, type: 'error' });
              } finally {
                setBookingLoading(false);
              }
            }}
            disabled={bookingLoading}
          >
            {bookingLoading ? (
              <span className="flex items-center justify-center">
                <svg className="animate-spin h-5 w-5 mr-2 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"></path>
                </svg>
                Cancelling...
              </span>
            ) : (
              'Cancel Waitlist'
            )}
          </button>
        );
      default:
        return null;
    }
  };

  const bookingButton = getBookingButton();

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40 p-4">
      {/* Recurring cancel modal overlays the details pane, does not replace it */}
      {showRecurringCancelModal && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40"
          onClick={() => setShowRecurringCancelModal(false)}
        >
          <div className="bg-white rounded-lg shadow-lg p-6 max-w-xs w-full flex flex-col items-center relative" onClick={e => e.stopPropagation()}>
            <button
              className="absolute top-2 right-2 text-gray-500 hover:text-gray-700 text-xl font-bold"
              onClick={() => setShowRecurringCancelModal(false)}
              aria-label="Close"
            >
              ×
            </button>
            <div className="text-lg font-bold mb-4 text-gray-800">Cancel Recurring Schedule</div>
            <div className="mb-6 text-gray-600 text-center">
              Would you like to cancel the entire recurring schedule, or just skip this specific class?
            </div>
            <div className="flex gap-3 w-full">
              {(() => {
                const { singleScheduleDisabled: skipDisabled, singleScheduleReason: skipReason } = getSingleScheduleDisabledAndReason(classItem);
                return (
                  <button
                    className={`flex-1 py-2 px-4 border border-gray-300 rounded-lg text-gray-700 ${skipDisabled ? 'opacity-50 cursor-not-allowed' : 'hover:bg-gray-50'}`}
                    onClick={() => !skipDisabled && handleRecurringCancelAction('skip')}
                    disabled={cancelLoading || skipDisabled}
                  >
                    Skip This Class Only
                    {skipDisabled && (
                      <div className="text-xs text-red-500 mt-1 whitespace-normal">{skipReason}</div>
                    )}
                  </button>
                );
              })()}
              <button
                className="flex-1 py-2 px-4 bg-red-600 text-white rounded-lg font-semibold hover:bg-red-700"
                onClick={() => handleRecurringCancelAction('entire')}
                disabled={cancelLoading}
              >
                Cancel Entire Schedule
              </button>
            </div>
          </div>
        </div>
      )}
      <div
        ref={modalRef}
        className="bg-white rounded-lg shadow-lg max-w-md w-full fixed left-1/2 -translate-x-1/2 top-6 bottom-[80px] flex flex-col overflow-y-auto"
      >
        {/* Scheduling Modal overlays (do not hide the pane) */}
        {showScheduling && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
            <SchedulingModal
              classItem={classItem}
              onClose={() => setShowScheduling(false)}
              onSchedule={handleSchedule}
              loading={scheduleLoading}
              initialOption="one-time"
              jwt={jwt}
            />
          </div>
        )}
        {showRecurringScheduling && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
            <SchedulingModal
              classItem={classItem}
              onClose={() => setShowRecurringScheduling(false)}
              onSchedule={handleSchedule}
              loading={scheduleLoading}
              initialOption="recurring"
              jwt={jwt}
            />
          </div>
        )}
        {/* Local Toast Notification inside the modal */}
        {toast.message && (
          <div
            className={`absolute top-4 left-1/2 transform -translate-x-1/2 z-50 px-6 py-3 rounded-lg shadow-lg font-semibold transition-all duration-300 ${toast.type === 'success' ? 'bg-green-100 border border-green-300 text-green-800' : 'bg-red-50 border border-red-200 text-red-700'}`}
            style={{ minWidth: '250px', maxWidth: '90vw', textAlign: 'center' }}
          >
            <button
              onClick={e => { e.stopPropagation(); setToast({ message: '', type: '' }); }}
              className="absolute top-1 right-2 text-lg font-bold focus:outline-none"
              aria-label="Close"
              style={{ background: 'none', border: 'none', cursor: 'pointer', lineHeight: 1 }}
            >
              ×
            </button>
            {toast.message}
          </div>
        )}
        {/* Header */}
        <div className="p-5 pb-3 border-b border-gray-200">
          <button
            className="absolute top-2 right-2 text-gray-500 hover:text-gray-700 text-2xl font-bold"
            onClick={onClose}
            aria-label="Close"
          >
            ×
          </button>
          <h2 className="text-2xl font-bold mb-1">{classItem.name}</h2>
          {classItem.trainer && (
            <div className="text-gray-700 mb-1 flex items-center gap-2">
              <span className="material-icons text-gray-500 text-lg">person</span>
              <span>Trainer: <span className="font-semibold">{classItem.trainer}</span></span>
            </div>
          )}
          <div className="text-gray-600 mb-1 flex items-center gap-2">
            <span className="material-icons text-gray-500 text-lg">schedule</span>
            <div>
              {DateTime.fromISO(classItem._raw.date + 'T' + classItem.start_time).toFormat('cccc, dd LLL yyyy')}<br/>
              {formatTime(classItem.start_time)} - {formatTime(classItem.end_time)}
            </div>
          </div>
          {description && (
            <div className="text-gray-800 whitespace-pre-line border-l-4 border-blue-200 pl-3 flex items-start gap-2">
              <span className="material-icons text-gray-500 text-lg mt-0.5">description</span>
              <div 
                className={/[\u0590-\u05FF]/.test(description) ? 'rtl text-right' : 'ltr text-left'}
                dir={/[\u0590-\u05FF]/.test(description) ? 'rtl' : 'ltr'}
              >
                {description}
              </div>
            </div>
          )}
        </div>
        {/* Members and Standby Users - merged into main pane */}
        <div className="p-5 pt-3 flex-1 overflow-y-auto pb-22">
          {/* Logbook Comment - displayed if available */}
          {logbookComment && (
            <div className="mb-4 p-3 bg-blue-50 border-l-4 border-blue-300 rounded">
              <h3 className="font-semibold mb-2 flex items-center gap-2 text-blue-900">
                <span className="material-icons text-blue-600 text-lg">description</span>
                Workout Details
              </h3>
              <div
                className={`text-sm text-blue-800 whitespace-pre-line ${/[\u0590-\u05FF]/.test(logbookComment) ? 'rtl text-right' : 'ltr text-left'}`}
                dir={/[\u0590-\u05FF]/.test(logbookComment) ? 'rtl' : 'ltr'}
              >
                {logbookComment}
              </div>
            </div>
          )}
          {/* Availability and credits information (if provided by this class) */}
          {(hasValidAvailability(classItem._raw) || classItem.isFull !== undefined || hasValidCredits(classItem._raw)) && (
            <div className="mb-4 space-y-3">
              {/* Availability/Full status */}
              {(hasValidAvailability(classItem._raw) || classItem.isFull !== undefined) && (
                <div className="mb-2">
                  <h3 className="font-semibold mb-1 flex items-center gap-2">
                    <span className="material-icons text-gray-500 text-lg">group</span>
                    Availability
                  </h3>
                  <div className="text-gray-800">
                    {hasValidAvailability(classItem._raw) ? (
                      <span>{classItem.availability} spots available</span>
                    ) : classItem.isFull === true ? (
                      <span className="text-red-600 font-semibold">Class is full</span>
                    ) : (
                      <span className="text-gray-500">Availability not shown</span>
                    )}
                  </div>
                </div>
              )}
              {/* Credits information */}
              {hasValidCredits(classItem._raw) && (
                <div className="mb-2">
                  <h3 className="font-semibold mb-1 flex items-center gap-2">
                    <span className="material-icons text-gray-500 text-lg">stars</span>
                    Credits
                  </h3>
                  <div className="text-gray-800 space-y-1">
                    {classItem.remainingCredits !== null && classItem.remainingCredits !== undefined && (
                      <div>Your credits: <span className="font-semibold">{classItem.remainingCredits}</span></div>
                    )}
                    <div>
                      Class cost: <span className="font-semibold">{classItem.creditPrice} credits</span>
                      {classItem.previousCreditPrice && classItem.previousCreditPrice !== classItem.creditPrice && (
                        <span className="text-gray-500 line-through ml-2">{classItem.previousCreditPrice}</span>
                      )}
                    </div>
                    {classItem.remainingCredits !== null && classItem.remainingCredits !== undefined && (
                      <div className={classItem.remainingCredits >= classItem.creditPrice ? 'text-green-600' : 'text-red-600'}>
                        {classItem.remainingCredits >= classItem.creditPrice ? (
                          '✓ You have enough credits'
                        ) : (
                          `✗ You need ${classItem.creditPrice - classItem.remainingCredits} more credits`
                        )}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
          {/* Registered Users - only if this class provides booked_users list and we know the count */}
          {hasBookedUsersList(classItem._raw) && hasValidRegisteredCount(classItem._raw) && (
            <div className="mb-2">
              <h3 className="font-semibold mb-1 flex items-center gap-2">
                <span className="material-icons text-gray-500 text-lg">group</span>
                Registered Users
                {capacityKnown && <span className="text-xs text-gray-500">{registeredCount}/{capacity}</span>}
                {!capacityKnown && <span className="text-xs text-gray-500">{registeredCount}</span>}
              </h3>
              <div className="grid grid-cols-1 gap-2">
                {bookedUsers.length === 0 && <div className="text-gray-400 text-sm">No users registered.</div>}
                {bookedUsers.map((user, idx) => {
                  const displayName = getUserDisplayName(user);
                  return (
                    <div key={idx} className="flex items-center gap-3 p-2 rounded hover:bg-gray-50">
                      <div className="w-8 h-8 rounded-full bg-gray-200 overflow-hidden flex items-center justify-center">
                        {user.image ? (
                          <img src={user.image} alt={displayName} className="w-full h-full object-cover" />
                        ) : (
                          <span className="text-sm font-medium text-gray-600">{getUserInitials(user)}</span>
                        )}
                      </div>
                      <span className="text-gray-800">{displayName}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
          {/* Standby Users - only if this class provides standby users list */}
          {hasStandbyUsersList(classItem._raw) && (
            <div className="mt-4">
              <h3 className="font-semibold mb-1 flex items-center gap-2">
                <span className="material-icons text-gray-500 text-lg">pending</span>
                Standby Users
                {capacityKnown && <span className="text-xs text-gray-500">{standbyUsers.length}/{capacity}</span>}
                {!capacityKnown && <span className="text-xs text-gray-500">{standbyUsers.length}</span>}
              </h3>
              <div className="grid grid-cols-1 gap-2">
                {standbyUsers.length === 0 && <div className="text-gray-400 text-sm">No users in standby.</div>}
                {standbyUsers.map((user, idx) => {
                  const displayName = getUserDisplayName(user, 'Standby user');
                  return (
                    <div key={idx} className="flex items-center gap-3 p-2 rounded hover:bg-gray-50">
                      <div className="w-8 h-8 rounded-full bg-gray-200 overflow-hidden flex items-center justify-center">
                        {user.image ? (
                          <img src={user.image} alt={displayName} className="w-full h-full object-cover" />
                        ) : (
                          <span className="text-sm font-medium text-gray-600">{getUserInitials(user)}</span>
                        )}
                      </div>
                      <span className="text-gray-800">{displayName}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
        {/* Booking Section pinned at bottom if present */}
        <div className="p-5 pt-3 border-t border-gray-200 bg-gray-50 sticky bottom-0">
          {classItem.excluded_from_schedule ? (
            <button
              className="w-full bg-gradient-to-r from-emerald-500 to-emerald-600 text-white py-3 px-4 rounded-lg font-semibold shadow-sm hover:shadow-md hover:brightness-105 transition-all flex items-center justify-center"
              onClick={async () => {
                try {
                  setScheduleLoading(true);
                  setError(null);
                  await axios.post(
                    `${API_BASE}/schedule/unskipOccurrence`,
                    {
                      schedule_id: classItem.schedule_id,
                      class_id: classItem._raw.id,
                    },
                    {
                      headers: { Authorization: `Bearer ${jwt}` },
                    }
                  );
                  setToast({ message: 'Class rescheduled successfully!', type: 'success' });
                  if (updateClassData) updateClassData(classItem._raw.id, { ...classItem, excluded_from_schedule: false });
                } catch (err) {
                  let errorMsg = 'Failed to reschedule class.';
                  if (err?.response?.data?.detail) {
                    errorMsg = err.response.data.detail;
                  } else if (err?.message) {
                    errorMsg = err.message;
                  }
                  setError(errorMsg);
                  setToast({ message: errorMsg, type: 'error' });
                } finally {
                  setScheduleLoading(false);
                }
              }}
              disabled={scheduleLoading}
            >
              {scheduleLoading ? (
                <span className="flex items-center justify-center">
                  <svg className="animate-spin h-5 w-5 mr-2 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"></path>
                  </svg>
                  Rescheduling...
                </span>
              ) : (
                'Reschedule class'
              )}
            </button>
          ) : classItem.schedule_id && !classItem.excluded_from_schedule ? (
            (() => {
              const { isBookable } = getClassTimingInfo(classItem);
              // Book/Standby button logic
              const isBookOrStandby = isBookable && bookingButton;
              if (isBookOrStandby) {
                return (
                  <div className="flex flex-row gap-3 w-full">
                    <div className="w-1/2">{bookingButton}</div>
                    <button
                      className="w-1/2 bg-gradient-to-r from-red-500 to-red-600 text-white py-3 px-4 rounded-lg font-semibold shadow-sm hover:shadow-md hover:brightness-105 transition-all flex items-center justify-center"
                      onClick={handleCancelSchedule}
                      disabled={scheduleLoading}
                    >
                      {scheduleLoading ? (
                        <span className="flex items-center justify-center">
                          <svg className="animate-spin h-5 w-5 mr-2 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"></path>
                          </svg>
                          Cancelling...
                        </span>
                      ) : (
                        'Cancel Schedule'
                      )}
                    </button>
                  </div>
                );
              } else {
                return (
                  <button
                    className="w-full bg-gradient-to-r from-red-500 to-red-600 text-white py-3 px-4 rounded-lg font-semibold shadow-sm hover:shadow-md hover:brightness-105 transition-all flex items-center justify-center"
                    onClick={handleCancelSchedule}
                    disabled={scheduleLoading}
                  >
                    {scheduleLoading ? (
                      <span className="flex items-center justify-center">
                        <svg className="animate-spin h-5 w-5 mr-2 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"></path>
                        </svg>
                        Cancelling...
                      </span>
                    ) : (
                      'Cancel Schedule'
                    )}
                  </button>
                );
              }
            })()
          ) : (
            <div className="flex flex-row gap-3 w-full">
              {bookingButton && bookingButton.type === 'button' && !bookingButton.props.children ? (
                <button
                  className="w-full bg-gradient-to-r from-blue-500 to-indigo-600 text-white py-3 px-4 rounded-lg font-semibold shadow-sm hover:shadow-md hover:brightness-105 transition-all flex items-center justify-center"
                  onClick={() => setShowRecurringScheduling(true)}
                  disabled={scheduleLoading}
                  type="button"
                >
                  Schedule
                </button>
              ) : bookingButton && bookingButton !== null ? (
                <div className="flex flex-row gap-3 w-full">
                  <div className="w-1/2">{bookingButton}</div>
                  <button
                    className="w-1/2 bg-gradient-to-r from-blue-500 to-indigo-600 text-white py-3 px-4 rounded-lg font-semibold shadow-sm hover:shadow-md hover:brightness-105 transition-all flex items-center justify-center"
                    onClick={() => setShowRecurringScheduling(true)}
                    disabled={scheduleLoading}
                    type="button"
                  >
                    Schedule
                  </button>
                </div>
              ) : (
                <button
                  className="w-full bg-gradient-to-r from-blue-500 to-indigo-600 text-white py-3 px-4 rounded-lg font-semibold shadow-sm hover:shadow-md hover:brightness-105 transition-all flex items-center justify-center"
                  onClick={() => setShowRecurringScheduling(true)}
                  disabled={scheduleLoading}
                  type="button"
                >
                  Schedule
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Classes({ jwt, onLogout }) {
  const [boxes, setBoxes] = useState([]);
  const [locations, setLocations] = useState([]);
  const [selectedBox, setSelectedBox] = useState(null);
  const [selectedBoxName, setSelectedBoxName] = useState(null);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [selectedDate, setSelectedDate] = useState('');
  const [schedule, setSchedule] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState('');
  const [, setError] = useState(null);
  const [selectedClass, setSelectedClass] = useState(null);
  const [dayWindowStart, setDayWindowStart] = useState(() => new Date());
  const [initialDataLoaded, setInitialDataLoaded] = useState(false);
  // Track previous box/location to detect actual switches (not just date changes)
  const prevBoxLocationRef = useRef({ box: null, location: null, date: null });

  // On mount: use cached box/location, fetch classes, then fetch boxes/locations in background
  useEffect(() => {
    // Set today's date as default
    setSelectedDate(new Date().toISOString().split('T')[0]);

    // Try to get last used box/location from localStorage
    const lastSelection = readLastClassesSelection();
    if (lastSelection?.box_id) {
      setSelectedBox(lastSelection.box_id);
    }
    if (lastSelection?.location_id) setSelectedLocation(lastSelection.location_id);

    // Fetch boxes/locations in background
    fetchBoxesAndLocationsBg(lastSelection);
    // Fetch user schedules on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jwt]);

  // When user changes box/location, update localStorage
  useEffect(() => {
    if (!selectedBox) return;
    const box = boxes.find(b => idsMatch(b.id, selectedBox));
    writeLastClassesSelection({
      appId: box?.app_id || null,
      boxId: box?.id ?? selectedBox,
      locationId: selectedLocation ?? null,
    });
  }, [boxes, selectedBox, selectedLocation]);

  // Background fetch for boxes/locations
  const fetchBoxesAndLocationsBg = async (cachedSelection) => {
    // First, fetch and update user applications from backend
    try {
      const appsRes = await axios.get(`${API_BASE}/applications`, {
        headers: { Authorization: `Bearer ${jwt}` },
      });
      if (appsRes.data.applications) {
        // Update localStorage with fresh application data
        const appsList = appsRes.data.applications.map(app => ({
          app_id: app.app_id,
          name: app.name,
          description: app.description,
          icon_path: app.icon_path,
          // Note: jwt_token and arbox_email would come from backend if needed
        }));
        localStorage.setItem('user_applications', JSON.stringify(appsList));
      }
    } catch (err) {
      console.error('❌ [DEBUG] Failed to fetch applications:', err);
    }

    // Check if we have user applications data
    const userApps = getUserApplications();
    const hasUserApps = userApps && userApps.length > 0;

    // Only show loading overlay if no cached data OR no user applications
    const hasCachedSelection = !!cachedSelection?.box_id;
    const showLoading = !(hasCachedSelection && hasUserApps);

    try {
      if (showLoading) {
        setLoading(true);
        if (!hasUserApps) {
          setLoadingMessage('Loading user applications...');
        } else {
          setLoadingMessage('Fetching boxes from all applications...');
        }
      }
      setError(null);

      // Fetch ALL boxes from ALL applications
      const boxesRes = await axios.get(`${API_BASE}/boxes/all-apps`, {
        headers: { Authorization: `Bearer ${jwt}` },
      });
      const fetchedBoxes = boxesRes.data.data || [];

      // Create and store box-to-app mapping
      const boxToAppMapping = createBoxToAppMapping(fetchedBoxes);
      setBoxToAppMap(boxToAppMapping);

      setBoxes(fetchedBoxes);
      if (showLoading) setLoadingMessage('Fetching locations from all applications...');

      // Fetch ALL locations from ALL applications
      const locationsRes = await axios.get(`${API_BASE}/boxes/locations/all-apps`, {
        headers: { Authorization: `Bearer ${jwt}` },
      });
      const allLocations = locationsRes.data.data || [];
      setLocations(allLocations);

      // Validate cached box against fresh backend data. If the gym was removed,
      // fall back silently instead of trusting stale browser state.
      const cachedBox = cachedSelection?.box_id;
      const cachedAppId = cachedSelection?.app_id;
      const cachedBoxData = cachedBox
        ? fetchedBoxes.find(b => idsMatch(b.id, cachedBox) && (!cachedAppId || b.app_id === cachedAppId))
        : null;
      const fallbackBox = fetchedBoxes[0] || null;
      const validBoxData = cachedBoxData || fallbackBox;
      const validBox = validBoxData?.id ?? null;

      if (!idsMatch(validBox, selectedBox) || validBox !== selectedBox) {
        setSelectedBox(validBox);
        if (validBoxData) {
          setSelectedBoxName(validBoxData.name);
        }
      } else if (selectedBox && !selectedBoxName) {
        // If box is already selected but name is missing, set it now
        const box = fetchedBoxes.find(b => idsMatch(b.id, selectedBox));
        if (box) {
          setSelectedBoxName(box.name);
        }
      }

      // Filter locations for the valid box
      const boxLocations = allLocations.filter(loc => idsMatch(loc.box_fk, validBox));
      // Validate cached location
      const cachedLocation = cachedSelection?.location_id;
      const cachedLocationData = cachedLocation
        ? boxLocations.find(l => idsMatch(l.id, cachedLocation))
        : null;
      let validLocation = cachedLocationData?.id ?? null;
      // If not valid, pick first available
      if (!validLocation && boxLocations.length) validLocation = boxLocations[0].id;
      if (!idsMatch(validLocation, selectedLocation) || validLocation !== selectedLocation) setSelectedLocation(validLocation);

      if (validBoxData) {
        writeLastClassesSelection({
          appId: validBoxData.app_id || null,
          boxId: validBox,
          locationId: validLocation ?? null,
        });
      }

      // Mark initial data as loaded
      setInitialDataLoaded(true);
    } catch (err) {
      console.error('❌ [DEBUG] Error in fetchBoxesAndLocationsBg:', err);
      console.error('❌ [DEBUG] Error details:', err.response?.data, err.message);
      setError(err.response?.data?.detail || err.message || 'Failed to fetch boxes/locations');
    } finally {
      if (showLoading) {
        setLoading(false);
        setLoadingMessage('');
      }
    }
  };

  const fetchSchedule = async () => {
    // For Move apps, use selectedBox when selectedLocation is not available
    // For other apps, require selectedLocation
    const locationId = selectedLocation || selectedBox;
    if (!locationId || !selectedDate) return;

    // Determine app_id and box_id
    let app_id;
    let box_id;

    if (selectedLocation) {
      // Traditional flow: get app_id from location's box
      const selectedLocationData = locations.find(loc => idsMatch(loc.id, selectedLocation));
      if (!selectedLocationData) {
        console.warn('Location data not found for selectedLocation:', selectedLocation);
        return;
      }
      box_id = selectedLocationData.box_fk;
      app_id = getAppIdForBox(box_id);
    } else {
      // Move app flow: use selectedBox directly
      box_id = selectedBox;
      app_id = getAppIdForBox(selectedBox);
    }

    // Fallback: if box-to-app mapping returns 'default' but user has no default app,
    // try to find the correct app by checking the box directly
    if (app_id === 'default') {
      // First, try to find the box in the current boxes list to get its app_id
      const boxData = boxes.find(box => idsMatch(box.id, box_id));
      if (boxData && boxData.app_id) {
        app_id = boxData.app_id;
        if (process.env.NODE_ENV === 'development') {
        }
      } else {
        // If box not found, try to get the first available app from user applications
        const userApps = getUserApplications();
        if (userApps.length > 0 && !userApps.some(app => app.app_id === 'default')) {
          const fallbackAppId = userApps[0].app_id;
          console.warn('⚠️ Using fallback app_id:', fallbackAppId, 'for box:', box_id, '- box not found in mapping or boxes list');
          app_id = fallbackAppId;
        }
      }
    }

    try {
      setLoadingMessage('');
      setLoading(true);
      setError(null);
      const res = await axios.post(
        `${API_BASE}/schedule/betweenDates`,
        {},
        {
          headers: { Authorization: `Bearer ${jwt}` },
          params: {
            from_date: selectedDate,
            to_date: selectedDate,
            location_id: locationId,
            app_id: app_id
          }
        }
      );
      const rawData = res.data.data || [];
      
      // Mark classes as scheduled if they match a user schedule
      const mapped = rawData.map((item, index) => {
        // Use mapApiResponseToClassItem to ensure all fields including isMoveClass are set
        const mappedItem = mapApiResponseToClassItem(item);
        // Ensure _raw contains the original item (mapApiResponseToClassItem already sets _raw, but ensure it's the full item)
        mappedItem._raw = item;
        
        // Log warnings for missing critical fields
        if (process.env.NODE_ENV === 'development') {
          if (!mappedItem.name || mappedItem.name === 'Class') {
            console.warn(`⚠️ Class ${index} missing name field:`, item);
          }
          if (!mappedItem.start_time) {
            console.warn(`⚠️ Class ${index} missing start_time field:`, item);
          }
        }
        
        return mappedItem;
      });
      
      setSchedule(mapped);
    } catch (err) {
      // Handle 401 errors gracefully - might be a temporary issue when switching apps
      if (err.response?.status === 401) {
        // Don't show error for 401 during app switching - it often resolves on retry
        // The useEffect will retry when dependencies change
        if (process.env.NODE_ENV === 'development') {
          console.warn('⚠️ 401 error fetching schedule (may resolve on retry):', err.response?.data?.detail || err.message);
        }
      } else {
        setError(err.response?.data?.detail || err.message || 'Failed to fetch schedule');
      }
    } finally {
      setLoading(false);
    }
  };

  // Auto-select first location when box changes (only for apps with locations)
  // This runs BEFORE the fetchSchedule useEffect to ensure selectedLocation is set correctly
  useEffect(() => {
    if (!selectedBox) return;
    
    // Check if the current box has any locations (even if global locations array has data from other apps)
    const boxLocations = locations.filter(loc => idsMatch(loc.box_fk, selectedBox));
    
    if (boxLocations.length > 0) {
      // Box has locations - ensure selectedLocation is valid for this box
      const currentLocationIsValid = boxLocations.some(loc => idsMatch(loc.id, selectedLocation));
      
      if (!currentLocationIsValid) {
        // Current location is invalid for this box (e.g., switched from Move to Arbox, or from one Arbox box to another)
        // Set the correct location immediately
        setSelectedLocation(boxLocations[0].id);
      }
      // If currentLocationIsValid is true, keep the existing selectedLocation
    } else {
      // No locations for this box (e.g., Move app) - clear selectedLocation immediately
      // This is important to prevent fetchSchedule from using the old location
      if (selectedLocation) {
        setSelectedLocation(null);
      }
    }
  }, [selectedBox, locations, selectedLocation]);

  useEffect(() => {
    // For Move apps (no locations), use selectedBox; for others, use selectedLocation
    // Important: For apps with locations (Arbox/Fizikal), we MUST wait for selectedLocation
    // to be set and valid for the current box. Don't use selectedBox as fallback for these apps,
    // as it would send box_id instead of location_id, causing 401 errors.
    
    if (!selectedBox || !selectedDate || !initialDataLoaded) return;
    
    // Check if the current selectedBox has any locations
    const boxLocations = locations.filter(loc => idsMatch(loc.box_fk, selectedBox));
    const boxHasLocations = boxLocations.length > 0;
    
    // Check if selectedLocation is valid for the current selectedBox
    let hasValidLocation = false;
    if (selectedLocation && boxHasLocations) {
      hasValidLocation = boxLocations.some(loc => idsMatch(loc.id, selectedLocation));
    }
    
    // For boxes without locations (Move app), we should NOT use selectedLocation
    // even if it's set (it might be from a previous box with locations)
    // So we only fetch if selectedLocation is null/cleared for boxes without locations
    // For boxes with locations, we must have a valid selectedLocation
    const shouldFetch = boxHasLocations 
      ? hasValidLocation 
      : !selectedLocation; // Only fetch for Move if selectedLocation is cleared
    
    if (shouldFetch) {
      // Check if box/location actually changed (not just date)
      const currentBoxLocation = { box: selectedBox, location: selectedLocation, date: selectedDate };
      const boxLocationChanged = 
        prevBoxLocationRef.current.box !== currentBoxLocation.box ||
        prevBoxLocationRef.current.location !== currentBoxLocation.location;
      
      // Update the ref for next comparison
      prevBoxLocationRef.current = currentBoxLocation;
      
      // Clear the old schedule immediately to show loading state instead of stale data
      setSchedule([]);
      setLoading(true);
      
      // Only show full-screen loading overlay when switching boxes/locations (not dates)
      // This prevents flicker when just changing dates
      if (boxLocationChanged) {
        setLoadingMessage('Loading classes...');
        // Clear selected class when location/box changes (e.g., when switching apps)
        setSelectedClass(null);
      } else {
        // Just date change - no full-screen overlay
        setLoadingMessage(''); // Clear any existing message
      }
      
      // Fetch immediately - no delay needed since:
      // 1. Box-to-app mapping is in localStorage (synchronous)
      // 2. Boxes state is already updated
      // 3. Fallback logic handles edge cases
      fetchSchedule();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLocation, selectedBox, selectedDate, locations, initialDataLoaded]);


  const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getLocationName = (locationId) => {
    const location = locations.find(loc => idsMatch(loc.id, locationId));
    return location ? location.location : 'Unknown Location';
  };

  // Searchable Box Selector Component
  const SearchableBoxSelect = ({ boxes: boxList, selectedBox, selectedBoxName, onBoxChange }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [filteredBoxes, setFilteredBoxes] = useState(boxList);
  const [isSearching, setIsSearching] = useState(false);
  const dropdownRef = useRef(null);
  const searchInputRef = useRef(null);
  const searchTimeoutRef = useRef(null);

    // Debounced search function that calls the backend
    useEffect(() => {
      // Clear previous timeout
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }

      // If search term is empty, use all boxes
      if (!searchTerm.trim()) {
        setFilteredBoxes(boxList);
        setIsSearching(false);
        return;
      }

      // Set loading state
      setIsSearching(true);

      // Debounce the API call
      searchTimeoutRef.current = setTimeout(async () => {
        try {
          const response = await axios.get(`${API_BASE}/boxes/all-apps`, {
            headers: { Authorization: `Bearer ${jwt}` },
            params: { search: searchTerm.trim() }
          });
          const fetchedBoxes = response.data.data || [];
          
          // Create and update box-to-app mapping
          const boxToAppMapping = createBoxToAppMapping(fetchedBoxes);
          setBoxToAppMap(boxToAppMapping);
          
          // Update filtered boxes for display
          setFilteredBoxes(fetchedBoxes);
        } catch (error) {
          console.error('Error searching boxes:', error);
          // On error, fall back to local filtering as backup
          const fallbackFiltered = boxList.filter(box => {
            const boxName = box.name?.toLowerCase() || '';
            const appDisplayName = box.app_id ? getApplicationDisplayName(box.app_id)?.toLowerCase() : '';
            const searchLower = searchTerm.toLowerCase();
            return boxName.includes(searchLower) || appDisplayName.includes(searchLower);
          });
          setFilteredBoxes(fallbackFiltered);
        } finally {
          setIsSearching(false);
        }
      }, 300); // 300ms debounce

      return () => {
        if (searchTimeoutRef.current) {
          clearTimeout(searchTimeoutRef.current);
        }
      };
    }, [searchTerm, boxList]);

    // Update filtered boxes when boxList changes (when no search is active)
    useEffect(() => {
      if (!searchTerm.trim()) {
        setFilteredBoxes(boxList);
      }
    }, [boxList, searchTerm]);

    // Close dropdown when clicking outside
    useEffect(() => {
      const handleClickOutside = (event) => {
        if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
          setIsOpen(false);
          setSearchTerm('');
          setFilteredBoxes(boxList); // Reset to all boxes when closing
        }
      };

      if (isOpen) {
        document.addEventListener('mousedown', handleClickOutside);
        // Focus search input when dropdown opens
        setTimeout(() => {
          if (searchInputRef.current) {
            searchInputRef.current.focus();
          }
        }, 100);
      }

      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }, [isOpen, boxList]);

    const handleBoxSelect = (box) => {
      // Pass the full box object to the parent, not just the ID
      onBoxChange(box);
      setIsOpen(false);
      setSearchTerm('');
      setFilteredBoxes(boxList); // Reset to all boxes after selection
    };

    const getDisplayText = () => {
      // Simply use the selectedBoxName prop - no lookup needed!
      if (!selectedBoxName) {
        return 'Select Gym';
      }
      
      // Check if we need to add app display name
      const box = filteredBoxes.find(b => idsMatch(b.id, selectedBox)) || boxList.find(b => idsMatch(b.id, selectedBox));
      if (box) {
        const appDisplayName = box.app_id ? getApplicationDisplayName(box.app_id) : '';
        if (hasMultipleApplications() && appDisplayName && appDisplayName !== selectedBoxName) {
          return `${selectedBoxName} (${appDisplayName})`;
        }
      }
      return selectedBoxName;
    };

    return (
      <div className="relative w-full max-w-xs" ref={dropdownRef}>
        {/* Trigger Button */}
        <button
          type="button"
          onClick={() => setIsOpen(!isOpen)}
          className="w-full py-2.5 px-4 border border-gray-300 rounded-lg bg-white text-left flex items-center justify-between focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-black text-sm shadow-sm hover:border-gray-400 transition-all duration-200"
        >
          <span className="truncate flex-1">{getDisplayText()}</span>
          <KeyboardArrowDownIcon
            className={`ml-2 text-gray-500 transition-transform duration-200 ${isOpen ? 'transform rotate-180' : ''}`}
            style={{ fontSize: '18px' }}
          />
        </button>

        {/* Dropdown Menu */}
        {isOpen && (
          <div className="absolute z-50 w-full mt-2 bg-white border border-gray-200 rounded-lg shadow-xl overflow-hidden" style={{ animation: 'fadeIn 0.2s ease-out' }}>
            {/* Search Input */}
            <div className="p-3 border-b border-gray-200 bg-gray-50">
              <div className="relative">
                <SearchIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" style={{ fontSize: '18px' }} />
                <input
                  ref={searchInputRef}
                  type="text"
                  placeholder="Search gyms..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>

            {/* Boxes List */}
            <div className="max-h-64 overflow-y-auto custom-scrollbar">
              {isSearching ? (
                <div className="px-4 py-8 text-center text-gray-500 text-sm">
                  Searching...
                </div>
              ) : filteredBoxes.length === 0 ? (
                <div className="px-4 py-8 text-center text-gray-500 text-sm">
                  No gyms found
                </div>
              ) : (
                filteredBoxes.map(box => {
                  const appDisplayName = box.app_id ? getApplicationDisplayName(box.app_id) : '';
                  const showAppName = hasMultipleApplications() && appDisplayName && appDisplayName !== box.name;
                  const isSelected = idsMatch(box.id, selectedBox);

                  return (
                    <button
                      key={box.id}
                      type="button"
                      onClick={() => handleBoxSelect(box)}
                      className={`w-full px-4 py-3 text-left hover:bg-blue-50 transition-colors duration-150 flex items-center justify-between ${
                        isSelected ? 'bg-blue-50 border-l-4 border-blue-500' : ''
                      }`}
                    >
                      <div className="flex flex-col flex-1 min-w-0">
                        <span className="text-sm font-medium text-gray-900 truncate">{box.name}</span>
                        {showAppName && (
                          <span className="text-xs text-gray-500 mt-0.5">{appDisplayName}</span>
                        )}
                      </div>
                      {isSelected && (
                        <CheckIcon className="ml-2 text-blue-600 flex-shrink-0" style={{ fontSize: '18px' }} />
                      )}
                    </button>
                  );
                })
              )}
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderHeader = () => {
    // Prepare fallback options for dropdowns if API data not loaded
    const cachedSelection = readLastClassesSelection();
    const cachedBox = cachedSelection?.box_id;
    const cachedLocation = cachedSelection?.location_id;
    const boxOptions = boxes.length
      ? boxes
      : cachedBox
        ? [{ id: cachedBox, name: selectedBoxName || 'Loading...', app_id: cachedSelection?.app_id || null }]
        : [];
    const locationOptions = locations.length
      ? locations.filter(loc => idsMatch(loc.box_fk, selectedBox || cachedBox))
      : cachedLocation
        ? [{ id: cachedLocation, location: 'Loading...' }]
        : [];

    // Check if the selected box has multiple locations (2 or more)
    const hasMultipleLocations = locationOptions.length > 1;

    return (
      <div className="flex flex-col gap-4 mb-6 pt-4 w-full box-border">
        {/* Gym and Location selection - stacked on mobile */}
        <div className="flex flex-col items-center gap-3 w-full box-border">
          {/* Modern Searchable Gym selection */}
          <SearchableBoxSelect
            boxes={boxOptions}
            selectedBox={selectedBox}
            selectedBoxName={selectedBoxName}
            onBoxChange={(box) => {
              setSelectedBox(box.id);
              setSelectedBoxName(box.name);
            }}
            jwt={jwt}
          />
          
          {/* Show location selector only if there are multiple locations */}
          {hasMultipleLocations && (
            <Select.Root
              value={selectedLocation ? String(selectedLocation) : ''}
              onValueChange={val => {
                const selectedLocationOption = locationOptions.find(loc => idsMatch(loc.id, val));
                setSelectedLocation(selectedLocationOption?.id ?? val);
              }}
            >
              <Select.Trigger className="w-full max-w-xs py-2 px-3 border border-gray-200 rounded-lg bg-white text-left flex items-center justify-between focus:outline-none focus:ring-2 focus:ring-blue-300 text-black text-sm mt-1">
                <Select.Value placeholder="Select Location">
                  {locationOptions.find(loc => idsMatch(loc.id, selectedLocation))?.location}
                </Select.Value>
                <Select.Icon className="ml-2 text-gray-400">▼</Select.Icon>
              </Select.Trigger>
              <Select.Portal>
                <Select.Content className="w-full max-w-xs min-w-[10rem] bg-white border border-gray-200 rounded-lg shadow-lg z-50">
                  <Select.Viewport>
                    {locationOptions.map(loc => (
                      <Select.Item key={loc.id} value={String(loc.id)} className="px-3 py-2 cursor-pointer hover:bg-blue-50 text-black text-sm">
                        <Select.ItemText>{loc.location}</Select.ItemText>
                        <Select.ItemIndicator className="ml-2 text-blue-600">✓</Select.ItemIndicator>
                      </Select.Item>
                    ))}
                  </Select.Viewport>
                </Select.Content>
              </Select.Portal>
            </Select.Root>
          )}
        </div>
      </div>
    );
  };

  const renderSchedule = () => {
    // Check if initial data is still loading
    const userApps = getUserApplications();
    const hasUserApps = userApps && userApps.length > 0;

    // For Move apps, locations.length will be 0, so only check boxes.length
    if (!initialDataLoaded || !hasUserApps || !boxes.length) {
      return (
        <div className="text-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-2"></div>
          <p>Loading...</p>
        </div>
      );
    }

    // For Move apps, selectedLocation is null, so check selectedBox instead
    const hasLocationOrBox = selectedLocation || selectedBox;
    if (!hasLocationOrBox || !selectedDate) {
      return (
        <div className="text-center py-8 text-gray-500">
          Please select a location and date to view classes.
        </div>
      );
    }

    if (loading) {
      return (
        <div className="text-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-2"></div>
          <p>Loading classes...</p>
        </div>
      );
    }

    if (!schedule.length) {
      // For Move apps, get box name instead of location name
      const locationOrBoxName = selectedLocation 
        ? getLocationName(selectedLocation)
        : (boxes.find(box => idsMatch(box.id, selectedBox))?.name || 'Unknown');
      return (
        <div className="text-center py-8 text-gray-500">
          No classes found for {formatDate(selectedDate)} at {locationOrBoxName}.
        </div>
      );
    }

    // Helper to render booking status/action labels
    const renderBookingLabel = (classItem) => {
      const labels = [];
      // Booked
      if (classItem.user_booked) {
        labels.push(<span key="booked" className="inline-block px-2 py-1 text-xs font-semibold rounded bg-green-100 text-green-800 mr-0.5">Booked</span>);
      }
      // Scheduled
      if (!!classItem.schedule_id && !classItem.excluded_from_schedule) {
        labels.push(<span key="scheduled" className="inline-block px-2 py-1 text-xs font-semibold rounded bg-purple-100 text-purple-800 mr-0.5">Scheduled</span>);
      }
      // In standby
      if (classItem.user_in_standby) {
        let standbyCount = 0;
        if (Array.isArray(classItem.stand_by)) {
          standbyCount = classItem.stand_by.length;
        } else if (typeof classItem.stand_by === 'number') {
          standbyCount = classItem.stand_by;
        } else if (classItem._raw && Array.isArray(classItem._raw.stand_by)) {
          standbyCount = classItem._raw.stand_by.length;
        } else if (classItem._raw && typeof classItem._raw.stand_by === 'number') {
          standbyCount = classItem._raw.stand_by;
        }
        labels.push(<span key="standby" className="inline-block px-2 py-1 text-xs font-semibold rounded bg-yellow-100 text-yellow-800 mr-0.5">
          In Standby{typeof classItem.stand_by_position === 'number' ? ` (${classItem.stand_by_position}/${standbyCount})` : ''}
        </span>);
      }
      // Full status (for apps that provide isFull field)
      if (classItem.isFull === true) {
        labels.push(<span key="full" className="inline-block px-2 py-1 text-xs font-semibold rounded bg-red-100 text-red-800 mr-0.5">Full</span>);
      }
      // --- SCHEDUABLE/BOOKABLE BADGE LOGIC FOR EXCLUDED ---
      const { isPast, isBookable } = getClassTimingInfo(classItem);
      if (!!classItem.schedule_id && classItem.excluded_from_schedule) {
        // Only show 'Scheduable' if not past and not bookable
        if (!isPast && !isBookable) {
          labels.push(<span key="scheduable-excluded" className="inline-block px-2 py-1 text-xs font-semibold rounded bg-cyan-100 text-cyan-800 mr-0.5">Scheduable</span>);
        }
        // If bookable, let the booking_option logic below show Bookable/Join Standby
      } else if (!isPast && !isBookable && !classItem.user_booked && !classItem.user_in_standby && !classItem.schedule_id) {
        // Only show 'Scheduable' for non-excluded, non-bookable, non-past, not already scheduled/booked/standby
        labels.push(<span key="scheduable" className="inline-block px-2 py-1 text-xs font-semibold rounded bg-cyan-100 text-cyan-800 mr-0.5">Scheduable</span>);
      }
      // Booking options
      switch (classItem.booking_option) {
        case 'insertScheduleUser':
          if (isBookable && !classItem.user_booked && !classItem.user_in_standby) {
            labels.push(<span key="bookable" className="inline-block px-2 py-1 text-xs font-semibold rounded bg-blue-100 text-blue-800 mr-0.5">Bookable</span>);
          }
          break;
        case 'insertStandby': {
          let standbyCount = 0;
          if (Array.isArray(classItem.stand_by)) {
            standbyCount = classItem.stand_by.length;
          } else if (classItem._raw && Array.isArray(classItem._raw.stand_by)) {
            standbyCount = classItem._raw.stand_by.length;
          } else if (typeof classItem.stand_by === 'number') {
            standbyCount = classItem.stand_by;
          }
          const standbyText = standbyCount > 0 ? ` +${standbyCount}` : '';
          labels.push(<span key="join-standby" className="inline-block px-2 py-1 text-xs font-semibold rounded bg-orange-100 text-orange-800 mr-0.5">Join Standby{standbyText}</span>);
          break;
        }
        case 'cancelScheduleUser':
          // Do not add a badge for cancel booking; only show the Booked badge
          break;
        case 'cancelWaitList':
          labels.push(<span key="cancel-waitlist" className="inline-block px-2 py-1 text-xs font-semibold rounded bg-red-100 text-red-800 mr-0.5">Cancel Waitlist</span>);
          break;
        case 'past':
          labels.push(<span key="past" className="inline-block px-2 py-1 text-xs font-semibold rounded bg-gray-200 text-gray-500 mr-0.5">Past</span>);
          break;
        default:
          break;
      }
      return labels;
    };

    // Debug: Log schedule before rendering

    return (
      <div className="space-y-4 w-full box-border">
        <div className="flex flex-col gap-3 w-full box-border">
          {schedule.map((classItem, index) => (
            <button
              key={index}
              className="w-full border rounded-lg p-3 md:p-4 bg-white shadow-sm box-border relative text-left hover:bg-blue-50 transition-colors"
              onClick={() => setSelectedClass(classItem)}
            >
              {/* First row: Title - Trainer */}
              <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-2 mb-3 w-full box-border">
                <div className="font-bold text-gray-800 text-base break-words w-full box-border">
                  {classItem.name}
                  {classItem.trainer && (
                    <span className="font-normal text-gray-600 flex items-center gap-1">
                      <span className="material-icons text-gray-400 text-sm">person</span>
                      {classItem.trainer}
                    </span>
                  )}
                </div>
                <div className="text-sm text-gray-600 font-medium whitespace-nowrap w-full box-border flex items-center gap-1">
                  <span className="material-icons text-gray-400 text-sm">schedule</span>
                  {formatTime(classItem.start_time)} - {formatTime(classItem.end_time)}
                </div>
              </div>
              {/* Booking status */}
              <div className="mb-3 flex flex-wrap items-center gap-2 w-full box-border">
                {renderBookingLabel(classItem)}
              </div>
              {/* Bottom row: Registration info and profile images */}
              <div className="flex justify-between items-center text-xs text-gray-500 w-full box-border">
                <div className="flex flex-col gap-1">
                  {/* Registration/Availability info */}
                  {(() => {
                    // Check what data is available for this specific class
                    const hasAvailability = hasValidAvailability(classItem._raw);
                    const hasRegistered = hasValidRegisteredCount(classItem._raw);
                    
                    // Prefer availability info if available
                    if (hasAvailability) {
                      return (
                        <span className="flex items-center gap-1">
                          <span className="material-icons text-gray-400 text-sm">group</span>
                          {classItem.availability} spots available
                        </span>
                      );
                    }
                    
                    // Show "Full" if isFull is true (even if we don't have exact availability)
                    if (classItem.isFull === true) {
                      return (
                        <span className="flex items-center gap-1">
                          <span className="material-icons text-gray-400 text-sm">group</span>
                          Full
                        </span>
                      );
                    }
                    
                    // Fall back to registered count if available
                    if (hasRegistered) {
                      const registered = classItem.registered;
                      const maxKnown = hasValidCapacity(classItem._raw);
                      const maxUsers = classItem.capacity || classItem.max_users;
                      
                      const registeredText = maxKnown
                        ? `${registered}/${maxUsers} registered`
                        : `${registered} registered`;
                      
                      return (
                        <span className="flex items-center gap-1">
                          <span className="material-icons text-gray-400 text-sm">group</span>
                          {registeredText}
                        </span>
                      );
                    }
                    
                    // Don't show anything if we don't have any registration info
                    return null;
                  })()}
                  {/* Credits info (if this class uses credits) */}
                  {hasValidCredits(classItem._raw) && (
                    <span className="flex items-center gap-1 text-xs">
                      <span className="material-icons text-gray-400 text-sm" style={{ fontSize: '14px' }}>stars</span>
                      {classItem.remainingCredits !== null && classItem.remainingCredits !== undefined ? (
                        <span>
                          {classItem.remainingCredits} credits available • {classItem.creditPrice} credits needed
                        </span>
                      ) : (
                        <span>{classItem.creditPrice} credits needed</span>
                      )}
                    </span>
                  )}
                </div>
                {/* Profile images - show at most 3 */}
                {Array.isArray(classItem._raw?.booked_users) && classItem._raw.booked_users.length > 0 && (
                  <div className="flex -space-x-2">
                    {classItem._raw.booked_users.slice(0, 3).map((user, userIndex) => {
                      const img = user.image;
                      const name = getUserDisplayName(user);
                      const initials = getUserInitials(user);
                      return (
                        <div key={userIndex} className="w-6 h-6 rounded-full border-2 border-white overflow-hidden bg-gray-200 flex items-center justify-center">
                          {img ? (
                            <img 
                              src={img} 
                              alt={name}
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <span className="text-xs font-medium text-gray-600">
                              {initials}
                            </span>
                          )}
                        </div>
                      );
                    })}
                    {classItem._raw.booked_users.length > 3 && (
                      <div className="w-6 h-6 rounded-full border-2 border-white bg-blue-100 flex items-center justify-center">
                        <span className="text-xs font-medium text-blue-600">
                          +{classItem._raw.booked_users.length - 3}
                        </span>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </button>
          ))}
        </div>
        {selectedClass && (
          <ClassDetailsPane
            classItem={selectedClass}
            onClose={() => setSelectedClass(null)}
            setError={setError}
            jwt={jwt}
            updateClassData={updateClassData}
            isClassScheduled={isClassScheduled}
            refreshSchedulesAndClasses={refreshSchedulesAndClasses}
            selectedLocation={selectedLocation}
            selectedDate={selectedDate}
          />
        )}
      </div>
    );
  };

  // Helper: get the start of the week (Sunday) for a given date
  const getWeekStart = (date) => {
    const d = new Date(date);
    const day = d.getDay(); // 0 (Sun) - 6 (Sat)
    return addDays(d, -day);
  };

  // Helper: get the 7 days of the week starting from Sunday
  const getWeekDays = (date) => {
    const start = getWeekStart(date);
    return Array.from({ length: 7 }, (_, i) => addDays(start, i));
  };

  // Modern week-based day picker, always Sun-Sat, no scrolling
  const renderDayPicker = () => {
    const weekStart = getWeekStart(dayWindowStart);
    const weekDays = getWeekDays(weekStart);
    const today = new Date();
    const todayStr = format(today, 'yyyy-MM-dd');
    const selectedStr = selectedDate;
    const monthYear = format(weekStart, 'LLLL yyyy');
    const handlePrev = () => {
      const newStart = addDays(weekStart, -7);
      setDayWindowStart(newStart);
      // If the new week contains today, select today; else, select first day
      const newEnd = addDays(newStart, 6);
      if (today >= newStart && today <= newEnd) {
        setSelectedDate(todayStr);
      } else {
        setSelectedDate(format(newStart, 'yyyy-MM-dd'));
      }
    };
    const handleNext = () => {
      const newStart = addDays(weekStart, 7);
      setDayWindowStart(newStart);
      // If the new week contains today, select today; else, select first day
      const newEnd = addDays(newStart, 6);
      if (today >= newStart && today <= newEnd) {
        setSelectedDate(todayStr);
      } else {
        setSelectedDate(format(newStart, 'yyyy-MM-dd'));
      }
    };
    return (
      <div className="mb-6 w-full">
        {/* Month/Year and arrows */}
        <div className="flex items-center justify-center mb-3 w-full relative">
          <button
            type="button"
            className="absolute left-1/4 transform -translate-x-1/2 px-2 py-1 rounded-full border border-gray-300 bg-white hover:bg-gray-100 text-gray-700"
            onClick={handlePrev}
            aria-label="Previous week"
          >
            &#8592;
          </button>
          <span className="text-lg font-semibold text-gray-800 text-center">{monthYear}</span>
          <button
            type="button"
            className="absolute right-1/4 transform translate-x-1/2 px-2 py-1 rounded-full border border-gray-300 bg-white hover:bg-gray-100 text-gray-700"
            onClick={handleNext}
            aria-label="Next week"
          >
            &#8594;
          </button>
        </div>
        {/* Days of week, always 7, no scroll */}
        <div className="grid grid-cols-7 gap-0 w-full">
          {weekDays.map((date, idx) => {
            const dateStr = format(date, 'yyyy-MM-dd');
            const isSelected = selectedStr === dateStr;
            const isToday = todayStr === dateStr;
            return (
              <button
                key={dateStr}
                className="flex flex-col items-center py-2 focus:outline-none"
                style={{ minWidth: 0 }}
                onClick={() => setSelectedDate(dateStr)}
              >
                <span className="text-xs font-medium text-gray-500 mb-1">{format(date, 'EEE')}</span>
                <span
                  className={
                    `w-8 h-8 flex items-center justify-center text-base font-bold ` +
                    (isSelected
                      ? 'border-2 border-green-500 rounded-full text-green-700 bg-green-50'
                      : isToday
                        ? 'border-2 border-green-400 rounded-full text-green-700 bg-green-50 opacity-60'
                        : 'text-gray-800')
                  }
                >
                  {format(date, 'd')}
                </span>
              </button>
            );
          })}
        </div>
      </div>
    );
  };

  // Update class data with fresh API response
  const updateClassData = (scheduleId, updatedClassItem) => {
    setSchedule(prev => {
      const newSchedule = prev.map(item => {
        return item._raw?.id === scheduleId
          ? { ...updatedClassItem }
          : item;
      });
      return newSchedule;
    });
    setSelectedClass(prev =>
      prev && prev._raw?.id === scheduleId
        ? { ...updatedClassItem }
        : prev
    );
  };

  // Helper to check if a class is scheduled
  const isClassScheduled = (item) => {
    // One-time match
    if (!!item.schedule_id) {
      return true;
    }
    // Recurring match
    if (!!item.schedule_id) {
      return true;
    }
    return false;
  };

  // Pass down a refreshSchedules function to ClassDetailsPane
  const refreshSchedulesAndClasses = async () => {
    // Re-fetch classes for the current day/location to update scheduled state
    await fetchSchedule();
    return []; // No user schedules to return here
  };

  return (
    <div className="min-h-screen bg-[#f8fbff] py-4 md:py-8 box-border">
      {/* Loading Overlay: Only for boxes/locations, not for classes */}
      {loadingMessage && (
        <div className="fixed inset-0 z-50 flex flex-col items-center justify-center bg-black bg-opacity-50">
          <div className="animate-spin rounded-full h-12 w-12 border-b-4 border-blue-600 mb-6"></div>
          <div className="text-white text-lg font-semibold drop-shadow-lg">{loadingMessage}</div>
        </div>
      )}
      <div className="max-w-4xl mx-auto px-4">
        <div className="overflow-hidden rounded-3xl border border-slate-200/80 bg-white shadow-[0_24px_70px_rgba(31,41,55,0.10)] w-full box-border">
          <BrandHeader title="Browse Classes" />
          {renderHeader()}
          <div className="pb-6 md:p-8 w-full box-border">

            {/* Modern Day Picker */}
            {renderDayPicker()}

            {/* Schedule Display */}
            <div className="mb-6 w-full box-border">
              {renderSchedule()}
            </div>
          </div>
        </div>
      </div>

      {/* Multi-App Debug Component (development only) */}
      <MultiAppDebug />
    </div>
  );
}

export default Classes;
